// Workspace strings, French first (the product's default locale) with
// English. Reads the app-wide locale from the existing i18n module, so the
// existing FR/EN switch drives these screens too.
import { getLocale } from "../i18n/index.js";

type Entry = string | ((p: Record<string, unknown>) => string);
type Dict = Record<string, Entry>;

const FR: Dict = {
  "app.name": "Espace SFCR",
  "app.reportingYear": p => `Exercice ${p.year}`,
  "app.processing": p => `${p.count} document(s) en traitement`,
  "app.allProcessed": "Aucun traitement en cours",
  "app.prototype": "Prototype",
  "nav.overview": "Vue d'ensemble",
  "nav.dashboard": "Tableau de bord",
  "nav.dossier": "Dossier",
  "nav.documents": "Documents sources",
  "nav.readiness": "Préparation des données",
  "nav.sfcr": "SFCR",
  "nav.chapter": p => `Chapitre ${p.code}`,
  "nav.review": "Revue",
  "nav.issues": "Points d'attention",
  "nav.reviewApproval": "Revue et validation",
  "nav.output": "Livrable",
  "nav.report": "Rapport généré",
  "nav.export": "Export",
  "nav.system": "Système",
  "nav.history": "Historique des traitements",
  "nav.audit": "Piste d'audit",
  "nav.admin": "Administration",
  "nav.collapse": "Réduire le menu",
  "nav.expand": "Déplier le menu",
  "nav.switchDossier": "Changer de dossier",
  "nav.notConnected": "Non connecté",
  "nav.dossiers": "Dossiers",
  "nav.current": "Dossier ouvert",
  "nav.newDossier": "Nouveau dossier",
  "nav.allDossiers": "Tous les dossiers",
  "cmd.open": "Rechercher",
  "cmd.label": "Rechercher une page ou une action",
  "cmd.placeholder": "Page, section ou action…",
  "cmd.empty": "Aucun résultat.",
  "cmd.actions": "Actions",
  "cmd.pages": "Pages",
  "cmd.sections": "Chapitres et sections",
  "cmd.language": "Langue : passer en anglais (English)",
  "cmd.move": "Naviguer",
  "cmd.select": "Ouvrir",

  "status.not_started": "Non démarré",
  "status.processing": "En traitement",
  "status.ready": "Prêt",
  "status.partial": "Partiellement prêt",
  "status.missing": "Manquant",
  "status.generated": "Généré",
  "status.needs_review": "À revoir",
  "status.approved": "Validé",
  "status.failed": "Échec",
  "status.pending": "En attente",

  "type.QRT": "QRT",
  "type.SFCR": "SFCR",
  "type.RSR": "RSR",
  "type.ACTUARIAL_REPORT": "Rapport actuariel",
  "type.FINANCIAL_REPORT": "Rapport financier",
  "type.ORG_CHART": "Organigramme",
  "type.GROUP_TEMPLATE": "Template groupe",
  "type.ORSA": "Rapport ORSA",
  "type.POLICY": "Politique écrite",
  "type.SUPPORTING_DOCUMENT": "Document d'appui",
  "type.UNKNOWN": "Non identifié",
  "role.N": "Année en cours",
  "role.N-1": "N-1",
  "role.other": "Autre période",
  "role.unknown": "Période inconnue",

  "stage.upload": "Téléversement",
  "stage.validate": "Contrôle du fichier",
  "stage.hash": "Empreinte et doublons",
  "stage.store": "Stockage",
  "stage.parse": "Lecture du document",
  "stage.metadata": "Métadonnées",
  "stage.classify": "Classification",
  "stage.period": "Période et entité",
  "stage.structure": "Structure (sections, tableaux)",
  "stage.qrt": "Extraction QRT",
  "stage.extract": "Extraction des points de données (texte)",
  "stage.index": "Indexation des preuves",

  "common.retry": "Réessayer",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.save": "Enregistrer",
  "common.remove": "Retirer",
  "common.restore": "Restaurer",
  "common.open": "Ouvrir",
  "common.inspect": "Détails",
  "common.loading": "Chargement",
  "common.requestId": p => `Référence : ${p.id}`,
  "common.networkError": "Le serveur est injoignable. Vérifiez la connexion puis réessayez.",
  "common.genericError": "La requête a échoué.",
  "common.notAvailable": "N/D",
  "readiness.READY": "Données complètes",
  "readiness.PARTIALLY_READY": "Données partielles",
  "readiness.NOT_READY": "Données requises manquantes",
  "boundary.title": "Cet écran n'a pas pu s'afficher",
  "boundary.body": "Les données du dossier ne sont pas affectées. Réessayez ou ouvrez une autre page.",
  "nav.chapterTitle": p => `Chapitre ${p.code} : ${p.title}`,
  "upload.withReason": p => `${p.state} : ${p.reason}`,
  "docs.newVersionOf": p => `Nouvelle version de ${p.name}`,
  "common.pages": p => `p. ${p.pages}`,

  "home.title": "Créer un dossier SFCR",
  "home.lead": "Un dossier regroupe les documents sources, la préparation des données et la production d'un rapport pour une entité et un exercice.",
  "home.name": "Nom du dossier",
  "home.entity": "Entité",
  "home.year": "Exercice de référence",
  "home.type": "Type de rapport",
  "home.create": "Créer le dossier",
  "home.delete": "Supprimer le dossier",
  "home.deleteTitle": p => `Supprimer « ${p.name} » ?`,
  "home.deleteBody": p => `Le dossier ${p.entity} (${p.year}) disparaît des listes. Ses documents, brouillons, validations et sa piste d'audit sont conservés : il peut être restauré.`,
  "home.deleteConfirm": p => `Tapez « ${p.name} » pour confirmer`,
  "home.deletedTitle": "Dossiers supprimés",
  "home.deletedHint": "Restaurables avec tout leur contenu",
  "home.deletedOn": p => `supprimé le ${p.date}${p.actor ? ` par ${p.actor}` : ""}`,
  "home.restore": "Restaurer",
  "home.select": "Dossiers existants",

  "overview.period": p => `Période de référence : ${p.year}`,
  "overview.inputs": "Données",
  "overview.inputsSub": "documents prêts",
  "overview.processing": "Traitement",
  "overview.processingSub": "documents traités",
  "overview.generation": "Agents IA",
  "overview.review": "Revue",
  "overview.notConnected": "Pas encore connecté",
  "overview.readiness": "Préparation des données",
  "overview.chapters": "Avancement des chapitres",
  "overview.attention": "Points d'attention",
  "next.eyebrow": "Prochaine étape",
  "next.import": "Importez les documents du dossier",
  "next.importBody": "QRT de l'exercice, SFCR/RSR N-1, rapport actuariel… Les données sont extraites à l'import et réparties par chapitre.",
  "next.processing": p => `Traitement de ${p.count} document(s) en cours`,
  "next.processingBody": "Leurs données apparaîtront dans les chapitres à la fin du traitement. Vous pouvez continuer à travailler.",
  "next.check": p => `${p.count} document(s) à vérifier`,
  "next.checkBody": "Confirmez ou corrigez-les pour que leurs données soient pleinement prises en compte.",
  "next.checkAction": "Vérifier les documents",
  "next.draft": p => `Lancez l'agent sur le chapitre ${p.code} : ${p.title}`,
  "next.draftBody": p => `${p.count} section(s) disposent des données nécessaires et attendent une exécution agent.`,
  "next.draftAction": "Ouvrir le chapitre",
  "next.review": "Relisez et validez les sorties agent",
  "next.reviewBody": p => `${p.approved} section(s) validée(s) sur ${p.total}.`,
  "next.reviewAction": "Ouvrir le rapport",
  "next.missing": p => `Il manque des données pour ${p.count} section(s)`,
  "next.missingBody": "Ajoutez les documents qui les contiennent pour pouvoir lancer l'agent.",
  "next.missingAction": "Voir ce qui manque",
  "next.export": "Le rapport est prêt à être exporté",
  "next.exportBody": "Toutes les sorties agent sont validées.",
  "next.exportAction": "Exporter en Word",
  "overview.attentionCount": p => `${p.count} point(s) requièrent votre attention`,
  "overview.allClear": "Aucun point bloquant pour le moment.",
  "overview.openReadiness": "Voir le détail",

  "docs.title": "Documents sources",
  "docs.lead": "Gérez les documents utilisés pour préparer le rapport : QRT, rapports N-1, rapports actuariels et documents d'appui.",
  "docs.add": "Ajouter des documents",
  "reason.entity": p => `Ce document semble concerner une autre entreprise (${p.entity}) : ses chiffres ne sont pas utilisés. Si c'est une erreur, corrigez l'entité.`,
  "reason.qrtRows": p => `${p.count} ligne(s) QRT illisible(s) : ces valeurs ne sont pas utilisées, les autres le sont.`,
  "reason.qrt": "Aucune valeur QRT n'a pu être lue dans ce fichier.",
  "reason.entityUnknown": "Entreprise non identifiée dans ce document : confirmez son entité pour utiliser ses données.",
  "draft.unconfirmed": p => `${p.count} document(s) ne sont pas utilisés tant que leur entreprise n'est pas confirmée (« Corriger » dans le détail du document).`,
  "reason.classification": "Type de document incertain : confirmez-le.",
  "reason.period": "Exercice non détecté : indiquez-le.",
  "reason.ocr": "Document scanné : une partie du texte n'a pas pu être lue.",
  "docs.filter.all": "Tous",
  "docs.filter.qrt": "QRT",
  "docs.filter.reports": "SFCR / RSR",
  "docs.filter.actuarial": "Actuariel",
  "docs.filter.financial": "Financier",
  "docs.filter.other": "Autres",
  "docs.filter.attention": "À traiter",
  "docs.col.document": "Document",
  "docs.col.type": "Type",
  "docs.col.period": "Période",
  "docs.col.processing": "Traitement",
  "docs.col.usedBy": "Utilisé par",
  "docs.col.actions": "Actions",
  "docs.empty.title": "Aucun document source",
  "docs.empty.body": "Ajoutez les QRT, le SFCR de l'année précédente et les documents d'appui pour commencer la préparation du dossier.",
  "docs.emptyFilter": "Aucun document ne correspond à ce filtre.",
  "docs.loadError": "Les documents n'ont pas pu être chargés.",
  "docs.dossierMissingNote": "Ce dossier n'existe pas dans la base active. Il a peut-être été créé sur un autre environnement, supprimé, ou perdu avant la configuration de la base persistante.",
  "docs.dossierMissingAction": "Revenir aux dossiers",
  "docs.corrected": "Corrigé par un utilisateur",
  "docs.confidence": p => `confiance ${p.value}`,
  "docs.reprocess": "Relancer le traitement",
  "docs.newVersion": "Nouvelle version",
  "docs.removeConfirmTitle": "Retirer ce document du dossier ?",
  "docs.removeConfirmBody": "Il ne sera plus utilisé pour la préparation. Ses versions et preuves restent conservées pour la piste d'audit.",
  "docs.removed": "Document retiré du dossier.",
  "docs.restored": "Document restauré dans le dossier.",
  "docs.restoreError": "Le document n'a pas pu être restauré.",
  "docs.failedShort": "Échec du traitement",

  "upload.title": "Ajouter des documents sources",
  "upload.drop": "Déposez vos documents ici",
  "upload.formats": "Formats acceptés : PDF, DOCX, XLSX et PPTX. 25 Mo maximum par fichier.",
  "upload.browse": "Parcourir les fichiers",
  "upload.hint": "Le type, l'exercice et l'entité sont détectés automatiquement à partir du contenu. Vous pourrez les corriger.",
  "upload.state.uploading": p => `Téléversement ${p.percent} %`,
  "upload.state.accepted": "Reçu, traitement en cours",
  "upload.state.duplicate": "Déjà importé",
  "upload.state.rejected": "Refusé",
  "upload.state.cancelled": "Téléversement annulé",
  "upload.state.error": "Échec du téléversement",
  "upload.viewExisting": "Voir le document existant",
  "upload.continue": "Vous pouvez fermer cette fenêtre : le traitement continue en arrière-plan.",
  "upload.done": "Terminé",
  "upload.summary": p => `${p.done}/${p.total} document(s) stabilisé(s)`,
  "upload.kpi.processing": p => `${p.count} en cours`,
  "upload.kpi.ready": p => `${p.count} prêt(s)`,
  "upload.kpi.review": p => `${p.count} à revoir`,
  "upload.kpi.duplicates": p => `${p.count} doublon(s)`,
  "upload.kpi.failed": p => `${p.count} échec(s)`,
  "upload.retryFailed": "Réessayer les échecs",
  "upload.cancelActive": "Annuler les uploads",

  "panel.metadata": "Métadonnées détectées",
  "panel.type": "Type de document",
  "panel.year": "Exercice",
  "panel.role": "Rôle",
  "panel.entity": "Entité",
  "panel.qrtUnit": "Unité des QRT",
  "panel.qrtUnitAsStated": "Comme indiqué dans le document",
  "panel.qrtUnit.EUR": "Euros (EUR)",
  "panel.qrtUnit.kEUR": "Milliers d'euros (kEUR)",
  "panel.qrtUnit.MEUR": "Millions d'euros (MEUR)",
  "panel.qrtUnitHint": "À corriger quand le libellé du tableau est faux (« in EUR 000 » au-dessus de montants en euros). S'applique aussitôt et lors des retraitements.",
  "panel.signals": "Signaux de classification",
  "panel.correct": "Corriger",
  "panel.correctTitle": "Corriger les métadonnées",
  "panel.correctHint": "Votre correction remplace la détection, est tracée dans la piste d'audit et est conservée lors des retraitements.",
  "panel.processing": "Traitement",
  "panel.review": "À vérifier",
  "panel.content": "Contenu extrait",
  "panel.dataPoints": "Points de données extraits",
  "panel.sections": "sections",
  "panel.tables": "tableaux",
  "panel.qrtFacts": "cellules QRT",
  "panel.evidence": "éléments de preuve",
  "panel.templates": "Templates QRT",
  "panel.versions": "Versions",
  "panel.version": p => `Version ${p.n}`,
  "panel.openOriginal": "Ouvrir l'original",
  "panel.warnings": "Avertissements",
  "panel.failedTitle": p => `${p.stage} a échoué`,
  "panel.failedOthers": "Les autres documents du dossier ne sont pas affectés.",
  "panel.attempt": p => `Tentative ${p.n}`,

  "ready.title": "Préparation des données",
  "ready.lead": "Pour chaque section, les données disponibles permettent-elles de rédiger avec des preuves ?",
  "ready.inProgress": p => `${p.count} document(s) encore en traitement : le résultat peut évoluer.`,
  "ready.required": "Requis",
  "ready.optional": "Optionnel",
  "ready.viaExtracted": "chiffres extraits du texte (pas de QRT)",
  "ready.state.satisfied": "Disponible",
  "ready.state.pending": "En attente de revue",
  "ready.state.missing": "Manquant",
  "ready.addDocument": "Ajouter un document",
  "ready.reviewDocument": "Revoir le document",
  "ready.sources": "Sources disponibles",
  "ready.loadError": "La préparation des données n'a pas pu être évaluée.",

  "section.tab.inputs": "Données d'entrée",
  "section.tab.sources": "Sources",
  "section.tab.draft": "Agent IA",
  "section.tab.validation": "Contrôles",
  "section.tab.history": "Historique",
  "section.sidebar": "Pilotage section",
  "section.requirementsReady": "Données prêtes",
  "section.requirementsRequired": "Requis",
  "section.previousReport": "Rapport précédent",
  "section.noPrevious": "Aucune section correspondante dans un rapport précédent du dossier.",
  "section.qrt": "Templates QRT",
  "section.noQrt": "Aucun template QRT requis disponible pour cette section.",
  "section.facts": p => `${p.count} cellules`,
  "section.showFacts": "Voir les cellules",
  "section.hideFacts": "Masquer",
  "section.gapTitle": "Agent non connecté",
  "section.gapBody": "L'exécution agent, le panneau de preuves et la validation de cette section nécessitent les endpoints de génération, pas encore exposés par le backend. L'espace de production prototype reste disponible, sur des données de démonstration.",
  "section.openPrototype": "Ouvrir l'espace de production (prototype)",
  "section.truncated": "Extrait tronqué.",

  "issues.title": "Points d'attention",
  "issues.lead": "File consolidée des éléments à traiter avant l'exécution agent et la validation.",
  "issues.col.severity": "Gravité",
  "issues.col.scope": "Périmètre",
  "issues.col.issue": "Point",
  "issues.col.action": "Action",
  "issues.severity.high": "Élevée",
  "issues.severity.medium": "Moyenne",
  "issues.severity.low": "Faible",
  "issues.input": "Données",
  "issues.empty": "Aucun point d'attention.",
  "issues.docFailed": p => `Le traitement de ${p.name} a échoué : ${p.message}`,
  "issues.docReview": p => `${p.name} : ${p.message}`,
  "issues.sectionMissing": p => `Donnée requise manquante : ${p.label}`,
  "issues.sectionPartial": p => `Donnée optionnelle manquante : ${p.label}`,
  "issues.sectionPending": p => `Document en attente de revue pour : ${p.label}`,

  "history.title": "Historique des traitements",
  "history.lead": "Traitements des documents du dossier : étapes, durée et résultat.",
  "history.empty": "Aucun traitement pour le moment.",
  "history.duration": p => `${p.seconds} s`,
  "history.details": "Détails techniques",

  "audit.title": "Piste d'audit",
  "audit.lead": "Qui a fait quoi, et quand, sur ce dossier.",
  "audit.action.generation.requested": "Exécution agent de section demandée",
  "audit.action.generation.completed": "Exécution agent de section terminée",
  "audit.action.generation.failed": "Échec de l'agent de section",
  "audit.action.chapter_generation.requested": "Exécution agent du chapitre demandée",
  "audit.action.chapter_generation.completed": "Exécution agent du chapitre terminée",
  "audit.action.chapter_generation.failed": "Échec de l'agent du chapitre",
  "audit.filter.category": "Type",
  "audit.filter.actor": "Auteur",
  "audit.category.all": "Tous",
  "audit.category.documents": "Documents",
  "audit.category.drafting": "Agents IA",
  "audit.category.corrections": "Corrections",
  "audit.category.dossier": "Dossier",
  "audit.actor.all": "Tous",
  "audit.actor.user": "Utilisateurs",
  "audit.actor.system": "Système",
  "audit.count": p => `${p.count} événement(s)`,
  "audit.runSummary": p => `${p.count} sections : ${p.states}`,
  "audit.findings": p => `${p.count} constat(s)`,
  "audit.gaps": p => `${p.count} information(s) manquante(s)`,
  "nav.audits": "Audits",
  "audits.title": "Audits du rapport",
  "audits.lead": "Trois audits indépendants : cohérence des chiffres dans le rapport, conformité réglementaire article par article, cohérence inter-millésimes (N-1 de ce rapport et N publié l'année précédente).",
  "audits.export": "Exporter en Excel",
  "audits.summary": "Synthèse des contrôles",
  "audits.kpi.total": "Total contrôles",
  "audits.kpi.ok": "OK",
  "audits.kpi.ko": "KO",
  "audits.kpi.review": "À revoir",
  "audits.kpi.na": "N/A",
  "audits.basis": p => `Calculé sur ${p.count} section(s) produite(s) par l'agent, le ${p.at}.`,
  "audits.tab.coherence": "Cohérence chiffres",
  "audits.tab.regulatory": "Réglementaire",
  "audits.tab.vintage": "Inter-millésimes",
  "audits.koCount": p => `${p.count} KO`,
  "audits.result.ok": "OK",
  "audits.result.ko": "KO",
  "audits.result.review": "À revoir",
  "audits.result.na": "N/A",
  "audits.col.result": "Résultat",
  "audits.col.aggregate": "Agrégat",
  "audits.col.appearances": "Apparitions",
  "audits.col.comment": "Commentaire",
  "audits.col.reference": "Référence",
  "audits.col.section": "Section",
  "audits.col.requirement": "Exigence",
  "audits.col.indicator": "Indicateur",
  "audits.col.current": "N-1 (ce rapport)",
  "audits.col.published": "N publié (rapport précédent)",
  "audits.col.difference": "Écart",
  "audits.text": "texte",
  "audits.unitAssumed": "unité supposée",
  "audits.automated": "Contrôle automatique",
  "audits.excerpt": "Passage concerné",
  "audits.noPublished": "Aucun SFCR ou RSR N-1 publié dans le dossier : ajoutez-le pour comparer les chiffres N-1 à ceux publiés l'année précédente.",
  "audits.empty.coherence": "Aucun chiffre cité dans les sorties agent pour l'instant. Lancez l'agent sur des sections pour lancer ce contrôle.",
  "audits.empty.regulatory": "Aucun contrôle réglementaire.",
  "audits.empty.vintage": "Aucune donnée N-1 à comparer.",
  "audits.emptyFilter": "Aucun contrôle ne correspond à ce filtre.",
  "audits.note": "Audits en temps réel : chaque ligne ouvre la section concernée, et relancer l'agent recalcule les audits. Un résultat OK signifie qu'aucun écart n'a été détecté ; la revue humaine reste requise.",
  "issues.audit": p => `${p.audit} · ${p.what} : ${p.comment}`,
  "issues.auditsNote": "Les constats des audits du rapport (cohérence des chiffres, contrôles réglementaires, inter-millésimes) sont repris dans cette file.",
  "issues.openAudits": "Ouvrir les audits",
  "audit.empty": "Aucun événement enregistré.",
  "audit.action.dossier.created": "Dossier créé",
  "audit.action.document.uploaded": "Document importé",
  "audit.action.document.version_added": "Nouvelle version importée",
  "audit.action.document.processed": "Document traité",
  "audit.action.document.processing_failed": "Échec du traitement",
  "audit.action.document.reprocess_requested": "Retraitement demandé",
  "audit.action.document.metadata_corrected": "Métadonnées corrigées",
  "audit.action.document.removed": "Document retiré",
  "audit.action.document.restored": "Document restauré",
  "audit.system": "Système",
  "locale.number": "fr-FR",
  "ind.primes_emises": "Primes émises brutes",
  "ind.primes_acquises": "Primes acquises brutes",
  "ind.charge_sinistres": "Charge de sinistres nette",
  "ind.frais_generaux": "Dépenses engagées",
  "ind.ratio_sinistres_primes": "Ratio sinistres sur primes",
  "ind.ratio_frais_primes": "Ratio frais sur primes",
  "ind.best_estimate": "Best estimate brut",
  "ind.marge_risque": "Marge pour risque",
  "ind.recouvrables_reassurance": "Recouvrables de réassurance",
  "ind.provisions_techniques": "Provisions techniques déclarées",
  "ind.provisions_techniques_brutes_calculees": "Provisions techniques brutes (BE et marge pour risque)",
  "ind.provisions_techniques_nettes_calculees": "Provisions techniques nettes de réassurance",
  "ind.best_estimate_vie": "Best estimate vie brut",
  "ind.marge_risque_vie": "Marge pour risque vie",
  "ind.recouvrables_vie": "Montants recouvrables vie",
  "ind.provisions_techniques_vie_total": "Provisions techniques vie (S.12.01)",
  "ind.provisions_techniques_vie_calculees": "Provisions techniques vie (BE et marge pour risque)",
  "ind.provisions_techniques_vie_nettes_calculees": "Provisions techniques vie nettes de réassurance",
  "ind.variation": p => `Variation : ${p.label}`,
  "confidence.eleve": "Confiance élevée",
  "confidence.moyen": "Confiance moyenne",
  "confidence.faible": "Confiance faible",
  "draft.agentInputs": "Données transmises à l'agent",
  "draft.canDraft": "Prêt pour l'agent",
  "draft.cannotDraft": "Données requises manquantes",
  "draft.col.indicator": "Indicateur",
  "draft.col.year": "Exercice",
  "draft.col.value": "Valeur",
  "draft.col.source": "Source",
  "draft.col.confidence": "Fiabilité",
  "draft.col.formula": "Calcul",
  "draft.derivedFromCells": "Calculé à partir de deux cellules du template",
  "draft.missingInputs": "Données non disponibles",
  "draft.inputsError": "Les données de la section n'ont pas pu être chargées.",
  "draft.loadError": "Les exécutions agent de la section n'ont pas pu être chargées.",
  "draft.startError": "L'agent n'a pas pu être lancé.",
  "draft.unsupportedTitle": "Agent IA non disponible",
  "draft.unsupportedBody": "Cette section ne fait pas partie des chapitres A à E couverts par l'agent IA.",
  "draft.state.none": "Agent non lancé",
  "draft.state.running": "Agent en cours",
  "draft.state.failed": "Échec de l'agent",
  "draft.state.produced": "Sortie agent à revoir",
  "draft.state.audit_ko": "Contrôle bloquant",
  "draft.state.escalated": "Données requises manquantes",
  "draft.state.empty": "Agent non lancé",
  "draft.aiGenerated": "Sortie agent, non revue",
  "draft.generate": "Lancer l'agent",
  "draft.regenerate": "Relancer l'agent",
  "draft.generating": "Agent en cours",
  "draft.runningBody": p => `L'agent analyse les données et produit la section ${p.code}. Temps écoulé : ${p.duration}. Vous pouvez poursuivre votre travail.`,
  "draft.failedTitle": "L'exécution agent a échoué",
  "draft.aiNotConfigured": "Le fournisseur d'IA n'est pas configuré sur le serveur.",
  "draft.escalatedTitle": "Agent interrompu : il manque des données",
  "draft.escalatedBody": "Les documents importés ne contiennent pas les informations requises (voir « Informations manquantes »). Ajoutez le document qui les contient, puis relancez l'agent.",
  "draft.emptyTitle": "L'agent n'a pas encore été lancé sur cette section",
  "draft.emptyBody": "L'agent utilise uniquement vos documents importés, et chaque phrase renvoie à sa source.",
  "draft.notReadyTitle": "Données requises manquantes",
  "draft.notReadyBody": "Ajoutez d'abord les documents requis (voir « Données d'entrée ») : sans eux, l'agent ne peut pas produire la section.",
  "draft.foreignTitle": "Les documents concernent une autre entreprise",
  "draft.foreignAll": p => `Les ${p.count} document(s) de ce dossier semblent concerner une autre entreprise que « ${p.entity} » : leurs données ne sont pas utilisées. Si c'est une erreur, corrigez l'entité de chaque document ; sinon importez les documents de « ${p.entity} ».`,
  "draft.foreignSome": p => `${p.count} document(s) du dossier sont ignorés car ils semblent concerner une autre entreprise que « ${p.entity} ».`,
  "draft.generatedBy": p => `Produit par l'agent le ${p.date}`,
  "draft.runDetails": p => `Modèle ${p.model} · ${p.seconds} · ${p.tokens} jetons · ${p.prompt}`,
  "gap.missingFact": p => `Donnée manquante : ${p.indicator} (${p.year})`,
  "gap.noSource": "Aucune source pour cette section : ajoutez le rapport N-1 ou un document d'appui.",
  "draft.tokens": p => `${p.count} jetons`,
  "draft.citation": p => `Voir la source ${p.n}`,
  "draft.figure": p => `Illustration du rapport N-1 conservée telle quelle (${p.id}) : à vérifier pour l'exercice.`,
  "draft.selectHint": "Sélectionnez une phrase ou un renvoi numéroté pour consulter les preuves.",
  "draft.missingInfo": "Informations manquantes",
  "draft.missingInfoHint": "Éléments que les sources ne permettent pas d'établir",
  "draft.derived": "Chiffres calculés",
  "draft.derivedHint": "Calculés par le serveur à partir des données sources, repris tels quels dans le texte",
  "rollover.button": "Nouveau millésime",
  "dossierEdit.button": "Modifier",
  "dossierEdit.title": "Modifier le dossier",
  "dossierEdit.name": "Nom du dossier",
  "dossierEdit.entity": "Entité (nom de l'entreprise)",
  "dossierEdit.entityNote": "Les documents seront revérifiés : ceux qui concernent cette entité seront utilisés, les autres signalés comme concernant une autre entreprise. Une entité corrigée à la main sur un document est conservée.",
  "dossierEdit.save": "Enregistrer",
  "dossierEdit.year": "Exercice de référence",
  "dossierEdit.yearNote": p => `Les rôles N / N-1 des documents seront recalculés pour l'exercice ${p.year} : par exemple, le SFCR de l'exercice ${Number(p.year) - 1} deviendra le rapport précédent (N-1). Un rôle corrigé à la main est conservé.`,
  "dossierEdit.error": "Le dossier n'a pas pu être modifié",
  "rollover.title": "Préparer l'exercice suivant",
  "rollover.body": "Un nouveau dossier est créé pour la même entité. L'équipe et la matrice RACI sont reprises, et les sections validées de ce rapport sont importées comme SFCR N-1 : les agents de l'exercice suivant partiront de ce texte validé. Les sections non validées ne sont pas reprises.",
  "rollover.year": "Exercice",
  "rollover.name": "Nom du dossier",
  "rollover.create": "Créer le dossier",
  "rollover.error": "Le dossier n'a pas pu être créé.",
  "audit.action.dossier.rolled_over": "Nouveau millésime créé",
  "nav.planning": "Planning",
  "planning.title": "Planning et échéances",
  "planning.lead": "Date de dépôt, échéances par chapitre ou par section, responsable de chaque bloc selon le RACI, et ce qui est en retard.",
  "planning.summary": "Synthèse du planning",
  "planning.filing": "Date de dépôt",
  "planning.noDate": "Non fixée",
  "planning.noFiling": "Fixez la date de dépôt : les chapitres et sections sans échéance propre en héritent.",
  "planning.kpi.approved": "Blocs validés",
  "planning.kpi.late": "En retard",
  "planning.kpi.due_soon": "À échéance sous 7 jours",
  "planning.kpi.without_owner": "Sans responsable",
  "planning.filter.all": "Tous les blocs",
  "planning.filter.late": "En retard",
  "planning.filter.soon": "Sous 7 jours",
  "planning.filter.open": "Non validés",
  "planning.owner": "Responsable (RACI)",
  "planning.due": "Échéance",
  "planning.delay": "Délai",
  "planning.status": "Revue",
  "planning.dueFor": p => `Échéance de ${p.code}`,
  "planning.inherited.chapter": p => `du chapitre : ${p.date}`,
  "planning.inherited.filing": p => `date de dépôt : ${p.date}`,
  "planning.noOwner": "À désigner dans Équipe et RACI",
  "planning.late": p => `${p.days} j de retard`,
  "planning.soon": p => Number(p.days) === 0 ? "Aujourd'hui" : `Dans ${p.days} j`,
  "planning.done": "Validé",
  "planning.note": "Un bloc est en retard quand son échéance est passée et qu'il n'est pas validé. Le responsable est le R du chapitre dans la matrice RACI. Seuls les approbateurs modifient les dates.",
  "audit.action.schedule.deadline_set": "Échéance modifiée",
  "nav.team": "Équipe et RACI",
  "team.title": "Équipe et RACI",
  "team.lead": "Les contributeurs du rapport et qui est responsable de quoi. Les demandes d'information d'une section vont par défaut au responsable (R) de son chapitre.",
  "team.contributors": "Contributeurs",
  "team.count": p => `${p.count} contributeur(s)`,
  "team.empty": "Aucun contributeur",
  "team.emptyBody": "Ajoutez les personnes à solliciter pour les informations manquantes.",
  "team.field.name": "Nom",
  "team.field.email": "E-mail",
  "team.field.role": "Fonction",
  "team.field.department": "Direction",
  "team.add": "Ajouter",
  "team.addError": "Le contributeur n'a pas pu être ajouté.",
  "team.saveError": "La modification n'a pas pu être enregistrée.",
  "team.status": "Statut",
  "team.active": "Actif",
  "team.inactive": "Inactif",
  "team.activate": "Réactiver",
  "team.deactivate": "Désactiver",
  "team.raci": "Matrice RACI",
  "team.raciHint": "Par chapitre ; les sections héritent de leur chapitre.",
  "team.scope": "Chapitre",
  "team.role.R": "Responsable",
  "team.role.A": "Approbateur",
  "team.role.C": "Consulté",
  "team.role.I": "Informé",
  "team.nobody": "Personne",
  "team.addPerson": "Ajouter…",
  "team.remove": p => `Retirer ${p.name}`,
  "team.note": "Quand l'envoi d'e-mails est configuré (SMTP), la demande part automatiquement ; sinon, la plateforme fournit le lien de réponse à transmettre (copie ou e-mail préparé).",
  "request.ask": "Demander à un contributeur",
  "request.new": "Nouvelle demande",
  "request.title": p => `Demander une information pour ${p.code}`,
  "request.template": p => `Pour la section ${p.code} du rapport, pouvez-vous fournir l'information suivante, avec sa source : ${p.gap}`,
  "request.to": "Destinataire",
  "request.choose": "Choisir un contributeur",
  "request.responsible": "responsable du chapitre",
  "request.noTeam": "Aucun contributeur : ajoutez-en dans Équipe et RACI.",
  "request.question": "Question",
  "request.create": "Créer la demande",
  "request.createError": "La demande n'a pas pu être créée.",
  "request.createdTitle": p => `Demande créée pour ${p.name}`,
  "request.createdBody": "Transmettez ce lien : il n'est affiché qu'une fois. La réponse deviendra une source de la section.",
  "request.emailed": p => `E-mail envoyé à ${p.email}. Le lien est aussi affiché ci-dessous, une seule fois.`,
  "request.emailFailed": "L'e-mail n'a pas pu être envoyé : transmettez le lien vous-même.",
  "request.link": "Lien de réponse",
  "request.copy": "Copier le lien",
  "request.copied": "Lien copié",
  "request.mail": "Préparer l'e-mail",
  "request.mailSubject": p => `SFCR ${p.entity} : information demandée pour la section ${p.code}`,
  "request.mailBody": p => `Bonjour ${p.name},\n\n${p.question}\n\nVous pouvez répondre directement ici : ${p.link}\n\nMerci.`,
  "request.listTitle": "Demandes aux contributeurs",
  "request.none": "Aucune demande pour cette section.",
  "request.meta": p => `À ${p.name}, le ${p.date}`,
  "request.status.open": "En attente",
  "request.status.answered": "Répondu",
  "request.status.cancelled": "Retirée",
  "request.cancel": "Retirer",
  "request.answeredNote": "Les réponses sont des sources de la section : relancez l'agent pour qu'il les cite.",
  "contribute.app": "Plateforme SFCR",
  "contribute.invalid": "Ce lien n'est pas valide.",
  "contribute.title": p => `Bonjour ${p.name}`,
  "contribute.context": p => `Une information est demandée pour la section ${p.code} « ${p.title} », par ${p.asker} le ${p.date}.`,
  "contribute.cancelled": "Cette demande a été retirée : aucune réponse n'est attendue.",
  "contribute.thanks": "Merci, votre réponse a été transmise.",
  "contribute.alreadyAnswered": "Réponse déjà transmise",
  "contribute.answer": "Votre réponse",
  "contribute.hint": "Indiquez la source de l'information (document, date, chiffre exact). Votre réponse sera citée dans le rapport.",
  "contribute.sendError": "La réponse n'a pas pu être envoyée.",
  "contribute.send": "Envoyer la réponse",
  "audit.action.team.contributor_added": "Contributeur ajouté",
  "audit.action.team.contributor_activated": "Contributeur réactivé",
  "audit.action.team.contributor_deactivated": "Contributeur désactivé",
  "audit.action.team.raci_updated": "RACI modifié",
  "audit.action.request.created": "Information demandée à un contributeur",
  "audit.action.request.answered": "Réponse d'un contributeur",
  "audit.action.request.cancelled": "Demande retirée",
  "nav.plan": "Plan du rapport",
  "plan.eyebrow": "Avant la production",
  "plan.title": "Plan du rapport à valider",
  "plan.lead": "L'architecte compare le rapport à produire au SFCR N-1 : correspondance des sections, conflits à arbitrer et modifications proposées, avant toute rédaction.",
  "plan.validate": "Valider le plan",
  "plan.saveError": "La décision n'a pas pu être enregistrée.",
  "plan.validatedTitle": p => `Plan validé par ${p.actor} le ${p.date}`,
  "plan.validatedBody": "Les décisions s'appliquent aux sources lues par les agents.",
  "plan.outdatedTitle": "Le plan a changé depuis sa validation",
  "plan.outdatedBody": "De nouveaux documents ont modifié les conflits ou les propositions. Arbitrez les nouveaux points puis validez à nouveau.",
  "plan.pendingBody": p => `${p.count} point(s) à arbitrer avant de valider le plan.`,
  "plan.noPrevious": "Aucun SFCR ou RSR N-1 dans le dossier : le plan ne peut pas reprendre la structure de l'exercice précédent.",
  "plan.noPreviousShort": "Pas de rapport N-1",
  "plan.summary": "Synthèse du plan",
  "plan.kpi.blocks": "blocs du rapport",
  "plan.kpi.kinds": "typologies",
  "plan.kpi.conflicts": "conflits à arbitrer",
  "plan.kpi.proposals": "modifications proposées",
  "plan.tree": "Arborescence proposée",
  "plan.col.block": "Bloc",
  "plan.col.kind": "Typologie",
  "plan.col.previous": "Section N-1",
  "plan.kind.quantitative": "Chiffres et interprétation",
  "plan.kind.narrative": "Narratif",
  "plan.kind.absence": "Constat (néant ou changement)",
  "plan.kind.chapter_lead": "Introduction de chapitre",
  "plan.kind.synthesis": "Synthèse",
  "plan.mapping.renumbered": "Numérotation différente",
  "plan.mapping.title_differs": "Titre différent",
  "plan.mapping.absent": "Absente du N-1",
  "plan.mapping.none": "Pas de N-1",
  "plan.items": "Conflits et propositions",
  "plan.filter.pending": "À arbitrer",
  "plan.filter.all": "Tous",
  "plan.nothingPending": "Tous les points sont arbitrés.",
  "plan.noItems": "Aucun conflit ni proposition.",
  "plan.severity.high": "Conflit",
  "plan.severity.medium": "À vérifier",
  "plan.severity.low": "Proposition",
  "plan.acknowledge": "Pris en compte",
  "plan.accept": "Accepter",
  "plan.reject": "Rejeter",
  "plan.decided.accepted": p => `Accepté par ${p.actor} le ${p.date}`,
  "plan.decided.rejected": p => `Rejeté par ${p.actor} le ${p.date}`,
  "plan.note": "Rejeter une correspondance retire cette section N-1 des sources de l'agent ; accepter une section sans équivalent l'ajoute aux sources de la section proposée. La rédaction reste possible avant la validation du plan.",
  "audit.action.plan.item_accepted": "Point du plan accepté",
  "audit.action.plan.item_rejected": "Point du plan rejeté",
  "audit.action.plan.validated": "Plan du rapport validé",
  "nav.users": "Utilisateurs",
  "auth.needs": p => `Réservé au rôle ${p.role} ou supérieur.`,
  "users.title": "Utilisateurs",
  "users.lead": "Les comptes de la plateforme et leur rôle. Un réviseur ne valide jamais une version qu'il a lui-même modifiée.",
  "users.offTitle": "Connexion désactivée",
  "users.offBody": "La plateforme fonctionne sans connexion (SFCR_AUTH_MODE=off). Activez SFCR_AUTH_MODE=local pour gérer des comptes.",
  "users.adminOnly": "Réservé aux administrateurs.",
  "users.list": "Comptes",
  "users.empty": "Aucun compte.",
  "users.role": "Rôle",
  "users.password": "Mot de passe initial",
  "users.passwordHint": "Au moins 12 caractères. Transmettez-le à la personne par un canal sûr.",
  "users.note": "Désactiver un compte ferme immédiatement ses sessions ouvertes. Toutes les modifications sont tracées.",
  "password.menu": "Mot de passe",
  "password.title": "Changer de mot de passe",
  "password.current": "Mot de passe actuel",
  "password.new": "Nouveau mot de passe",
  "password.again": "Confirmer le nouveau mot de passe",
  "password.mismatch": "Les deux saisies diffèrent.",
  "password.hint": "Au moins 12 caractères. Vos autres sessions ouvertes seront fermées.",
  "password.save": "Enregistrer",
  "password.done": "Mot de passe modifié.",
  "password.error": "Le mot de passe n'a pas pu être modifié.",
  "password.reset": "Réinitialiser le mot de passe",
  "password.resetTitle": p => `Réinitialiser le mot de passe de ${p.name}`,
  "password.resetHint": "Au moins 12 caractères. Transmettez-le par un canal sûr ; les sessions ouvertes de la personne sont fermées.",
  "password.resetDone": "Mot de passe réinitialisé.",
  "audit.action.auth.password_changed": "Mot de passe modifié",
  "audit.action.auth.password_reset": "Mot de passe réinitialisé",
  "login.title": "Connexion",
  "login.lead": "Connectez-vous pour accéder à la plateforme SFCR.",
  "login.email": "E-mail",
  "login.password": "Mot de passe",
  "login.submit": "Se connecter",
  "login.error": "La connexion a échoué.",
  "login.note": "Les sessions expirent après 8 heures. Les comptes sont créés par un administrateur.",
  "login.signOut": "Se déconnecter",
  "role.user.preparer": "Préparateur",
  "role.user.reviewer": "Réviseur",
  "role.user.approver": "Approbateur",
  "role.user.admin": "Administrateur",
  "audit.action.auth.login": "Connexion",
  "audit.action.auth.login_failed": "Échec de connexion",
  "audit.action.auth.user_created": "Utilisateur créé",
  "audit.action.auth.user_updated": "Utilisateur modifié",
  "refv.default": "Version d'origine",
  "refv.version": p => `Version ${p.n}`,
  "refv.propose": "Proposer une modification",
  "refv.proposeTitle": p => `Proposer une modification : ${p.label}`,
  "refv.newCriteria": "Nouveaux critères de validation",
  "refv.newInstructions": "Nouvelles instructions de rédaction",
  "refv.reason": "Raison de la modification (obligatoire)",
  "refv.proposeNote": "La modification ne s'applique qu'après approbation par un administrateur ; l'auteur ne peut pas l'approuver lui-même.",
  "refv.submit": "Proposer",
  "refv.error": "L'action n'a pas pu être enregistrée.",
  "refv.history": "Historique",
  "refv.noHistory": "Aucune modification : la version d'origine est en vigueur.",
  "refv.proposedBy": p => `Proposée par ${p.actor} le ${p.date}`,
  "refv.decidedBy": p => `décidée par ${p.actor} le ${p.date}`,
  "refv.status.proposed": "En attente",
  "refv.status.approved": "Approuvée",
  "refv.status.rejected": "Rejetée",
  "refv.pendingTitle": p => `Version ${p.n} proposée par ${p.actor}`,
  "refv.pendingCount": p => `${p.count} en attente`,
  "refv.approve": "Approuver",
  "refv.reject": "Rejeter",
  "refv.confirmReject": "Confirmer le rejet",
  "refv.rejectReason": "Raison du rejet (obligatoire)",
  "refv.waitingAdmin": "En attente d'approbation par un administrateur.",
  "audit.action.referential.proposed": "Modification du référentiel proposée",
  "audit.action.referential.approved": "Modification du référentiel approuvée",
  "audit.action.referential.rejected": "Modification du référentiel rejetée",
  "nav.referential": "Référentiel",
  "ref.eyebrow": "Administration",
  "ref.title": "Référentiel des agents",
  "ref.lead": "Ce que les agents utilisent pour rédiger et contrôler : articles du règlement délégué et leurs critères, catalogue des sections, configuration de rédaction.",
  "ref.tab.articles": "Articles réglementaires",
  "ref.tab.sections": "Catalogue des sections",
  "ref.tab.drafting": "Agents et règles",
  "ref.col.reference": "Référence",
  "ref.col.title": "Article",
  "ref.col.criteria": "Critères de validation",
  "ref.col.sections": "Sections",
  "ref.col.control": "Contrôle",
  "ref.col.block": "Bloc",
  "ref.col.kind": "Typologie",
  "ref.col.instructions": "Instructions de rédaction",
  "ref.col.inputs": "Données",
  "ref.col.checks": "Contrôles",
  "ref.automated": "Automatique",
  "ref.manual": "Revue humaine",
  "ref.required": "Requis :",
  "ref.config": "Configuration de rédaction",
  "ref.configured": "Configuré",
  "ref.notConfigured": "Non configuré",
  "ref.mode": "Fournisseur",
  "ref.deployment": "Modèle (déploiement)",
  "ref.promptVersion": "Version du prompt",
  "ref.guardrails": "Contrôles appliqués",
  "ref.guardrail.citations": "citations vérifiées",
  "ref.guardrail.figures": "chiffres sourcés",
  "ref.guardrail.markers": "renvois alignés",
  "ref.guardrail.articles": "articles",
  "ref.guardrail.identities": "identités QRT",
  "ref.guardrail.style": "longueur N-1",
  "ref.guardrail.changes": "variations expliquées",
  "ref.rule": p => `Règle : ${p.kind}`,
  "ref.note": "Modifier un critère ou une instruction change ce que les agents écrivent et contrôlent : chaque modification est une version proposée, appliquée seulement après approbation, et chaque rédaction enregistre les versions utilisées.",
  "draft.layout.label": "Disposition",
  "draft.layout.side": "Points à vérifier à droite",
  "draft.layout.stack": "Disposition verticale",
  "draft.insights": "À vérifier",
  "draft.findingsTitle": "Contrôles",
  "draft.findingsCount": p => `${p.blocking} bloquant(s), ${p.warnings} à vérifier`,
  "draft.noFindings": "Aucun constat sur ce texte",
  "chain.title": "Chaîne de production",
  "chain.summary": p => `${p.done} étape(s) terminée(s) sur ${p.total}`,
  "chain.human": "humain",
  "chain.step.extract": "Extraction des chiffres QRT",
  "chain.step.compute": "Calculs du serveur",
  "chain.step.prechecks": "Contrôles des données",
  "chain.step.sources": "Recherche des sources",
  "chain.step.draft": "Rédaction par l'agent",
  "chain.step.citations": "Vérification des citations",
  "chain.step.figures": "Contrôle des chiffres et des renvois",
  "chain.step.regulatory": "Contrôles réglementaires",
  "chain.step.escalation": "Informations demandées",
  "chain.step.review": "Revue et validation",
  "chain.state.done": "Terminé",
  "chain.state.ko": "Bloquant",
  "chain.state.warning": "À vérifier",
  "chain.state.skipped": "Sans objet",
  "chain.state.pending": "En attente",
  "chain.state.running": "En cours",
  "chain.extractDetail": p => `${p.facts} cellule(s) QRT reprise(s)${Number(p.missing) ? `, ${p.missing} manquante(s)` : ""}.`,
  "chain.noFacts": "Aucune cellule QRT pour cette section.",
  "chain.computeDetail": p => `${p.count} chiffre(s) calculé(s) : ratios, totaux, variations.`,
  "chain.noFinding": "Aucun constat.",
  "chain.sourcesDetail": p => `${p.count} passage(s) : rapport N-1, documents d'appui${Number(p.answers) ? `, ${p.answers} réponse(s) de contributeurs` : ""}.`,
  "chain.escalated": "Rédaction suspendue : données requises manquantes.",
  "chain.draftDetail": p => `Brouillon produit par ${p.model}.`,
  "chain.citationsDetail": p => `${p.count} citation(s) vérifiée(s) contre les sources.`,
  "chain.requestsDetail": p => `${p.open} demande(s) en attente, ${p.answered} répondue(s).`,
  "chain.gapsDetail": p => `${p.count} information(s) manquante(s), aucune demande envoyée.`,
  "chain.noGap": "Aucune information manquante.",
  "check.style": "Longueur cohérente avec la section N-1",
  "check.markers": "Chaque renvoi est placé à côté du montant de la cellule qu'il cite",
  "section.tab.comparison": "Comparaison N-1",
  "lang.fr": "français",
  "lang.en": "anglais",
  "compare.noPrevious": "Pas de section N-1 à comparer",
  "compare.noPreviousBody": "Le dossier ne contient pas de SFCR ou RSR N-1 de cette entité pour cette section, ou le plan a écarté la correspondance.",
  "compare.source": p => `${p.name} · ${p.sections}`,
  "compare.changed": p => `${p.share} % du texte modifié`,
  "compare.noCurrent": "La section n'est pas encore rédigée : seul le texte N-1 est affiché.",
  "compare.noCurrentShort": "Section non rédigée.",
  "compare.languages": p => `Les deux textes ne sont pas dans la même langue (N-1 : ${p.before}, actuel : ${p.after}) : ils sont présentés côte à côte, sans surlignage.`,
  "compare.tooLong": "Textes trop longs pour un surlignage mot à mot : ils sont présentés côte à côte.",
  "compare.diffTitle": "Évolution par rapport au N-1",
  "compare.removed": "retiré",
  "compare.added": "ajouté",
  "compare.previousTitle": "Texte N-1",
  "compare.currentTitle": "Texte actuel",
  "table.delta": "Écart N/N-1",
  "table.deltaPct": "Écart %",
  "table.na": "n.d.",
  "table.computed": "calculé",
  "table.noPrior": "N-1 non disponible : ajoutez les QRT N-1 ou le SFCR N-1 pour obtenir la variation. Montants arrondis : valeur exacte et source au survol.",
  "nav.datapoints": "Points de données",
  "dp.title": "Points de données",
  "dp.lead": "Chaque indicateur extrait à l'import, des QRT ou du texte des documents (SFCR/RSR, rapport actuariel...) : les valeurs N et N-1, l'écart, la source et les chapitres et sections qui l'utilisent.",
  "dp.kpi.from_text": "indicateurs issus du texte",
  "dp.chapter": "Chapitre",
  "dp.method.qrt": "QRT",
  "dp.method.text": "Texte",
  "dp.chapterTitle": "Points de données du chapitre",
  "dp.chapterEmpty": "Aucun point de données extrait pour ce chapitre. Importez les QRT ou un document qui les mentionne (SFCR N-1, rapport actuariel...).",
  "dp.viewAll": "Tous les points de données",
  "dp.export": "Exporter en Excel",
  "dp.summary": "Synthèse des points de données",
  "dp.kpi.found_n": "points de données N",
  "dp.kpi.found_n1": "points de données N-1",
  "dp.kpi.with_change": "écarts N/N-1 calculés",
  "dp.kpi.indicators": "indicateurs suivis",
  "dp.noPrior": "Aucune valeur N-1 dans le dossier : ajoutez les QRT N-1 ou le SFCR N-1 pour calculer les écarts.",
  "dp.template": "Template",
  "dp.datapoint": "Data point (QRT)",
  "dp.source": "Source",
  "dp.usedBy": "Utilisé par",
  "dp.notes": "Notes d'extraction",
  "dp.note": "Une cellule QRT prime toujours. À défaut, la valeur vient du texte d'un document : le modèle la localise et la recopie, le serveur vérifie qu'elle figure mot pour mot dans le passage et la convertit en k€. Ces valeurs sont celles reçues par les agents.",
  "table.indicator": "Indicateur",
  "table.var": "Var.",
  "table.hoverNote": "Montants arrondis : valeur exacte et sources au survol.",
  "table.provenance": "Sources et validation",
  "table.conflicting_sources": "Sources contradictoires",
  "table.carried_forward": "Exercice precedent : a confirmer pour N",
  "table.pending_review": "A valider",
  "review.staleSources": "Les sources ont change. La validation precedente n'est plus valable.",
  "unit.bn": "Md€",
  "unit.m": "M€",
  "table.variation": "Var.",
  "table.note": "Tableau établi par le serveur à partir des cellules QRT et des chiffres calculés. Montants arrondis : valeur exacte et source au survol.",
  "nav.synthesis": "Synthèse",
  "nav.exportHint": "Télécharger le rapport au format Word",
  "report.title": "Le rapport",
  "report.referenceTemplate": "Modèle de rapport",
  "report.referenceTemplateLead": "Modèle SFCR SFPJ avec les narratifs, les tableaux et les références sources.",
  "report.referenceTemplateNote": "Modèle de référence SFPJ 2025 : les valeurs XXX sont à compléter. Cet aperçu n'est pas alimenté par les données du dossier et ne modifie pas le rapport généré.",
  "report.backToReport": "Retour au rapport",
  "report.openTemplate": "Ouvrir en plein écran",
  "report.downloadTemplate": "Télécharger le HTML",
  "report.lead": "Le rapport en un seul document : synthèse, puis chapitres A à E, avec le texte courant de chaque section et son statut de revue.",
  "report.export": "Exporter en Word",
  "report.exportPdf": "Exporter en PDF",
  "report.moreExports": "Autres formats",
  "report.templateOn": p => `Export Word : modèle de l'entreprise appliqué (styles, en-têtes et pieds de page), mis à jour le ${p.date}.`,
  "report.templateOff": "Export Word : mise en page standard. Ajoutez le modèle Word de l'entreprise pour reprendre ses styles, en-têtes et pieds de page.",
  "report.templateUpload": "Ajouter le modèle Word",
  "report.templateReplace": "Remplacer le modèle",
  "report.templateRemove": "Retirer le modèle",
  "report.templateError": "Le modèle n'a pas pu être enregistré.",
  "report.print": "Imprimer / PDF",
  "report.printStatus": p => `${p.approved} section(s) validée(s) sur ${p.total}`,
  "report.printDraft": "Sortie agent, non validée.",
  "report.progress": "Avancement du rapport",
  "report.kpi.drafted": "Sections produites",
  "report.kpi.approved": "Sections validées",
  "report.kpi.changes": "Modifications demandées",
  "report.kpi.export": "Export",
  "report.exportNote": "Les sections non validées sont marquées comme sorties agent et chaque section liste ses sources.",
  "report.filter": "Afficher",
  "report.filter.all": "Toutes les sections",
  "report.filter.to_review": "À valider",
  "report.filter.approved": "Validées",
  "report.filter.missing": "Agent non lancé",
  "report.approvedBy": p => `Validé par ${p.actor} le ${p.date}`,
  "report.notDrafted": "Agent non lancé pour cette section.",
  "report.emptyFilter": "Aucune section ne correspond à ce filtre.",
  "report.note": "Le rapport reflète le texte courant de chaque section : la dernière version humaine ou, à défaut, la dernière sortie agent. Sélectionnez une phrase pour ouvrir la section, ses preuves et sa revue.",
  "synthesis.eyebrow": "Article 292",
  "synthesis.title": "Synthèse",
  "synthesis.lead": "La synthèse placée en tête du rapport, rédigée à partir des introductions des chapitres A à E et de la section E.2. Lancez d'abord l'agent sur les chapitres.",
  "synthesis.openReport": "Voir le rapport",
  "audit.action.report.exported": "Rapport exporté",
  "audit.action.report.template_set": "Modèle Word ajouté",
  "audit.action.report.template_removed": "Modèle Word retiré",
  "review.status.none": "Agent non lancé",
  "review.status.ai_draft": "Sortie agent à valider",
  "review.status.edited": "Modifié, à valider",
  "review.status.changes_requested": "Modifications demandées",
  "review.status.approved": "Validé",
  "review.action.edit": "Modifier",
  "review.action.approved": "Valider",
  "review.action.changes_requested": "Demander des modifications",
  "review.dialog.approved": "Valider la section",
  "review.dialog.changes_requested": "Demander des modifications",
  "review.blockingTitle": p => `${p.count} constat(s) bloquant(s) sur ce texte`,
  "review.justification": "Justification de la validation (obligatoire)",
  "review.comment": "Modifications attendues (obligatoire)",
  "review.commentOptional": "Commentaire (facultatif)",
  "review.saveError": "L'enregistrement a échoué.",
  "review.loadError": "Impossible de charger la revue de la section.",
  "review.editTitle": "Modifier le texte",
  "review.editHint": p => Number(p.count) ? `Conservez les renvois [1] à [${p.count}] à côté des affirmations qu'ils justifient.` : "Cette sortie agent ne cite aucune source.",
  "review.changedNote": "Le texte a changé entre-temps. Rechargez la section, puis reprenez la modification.",
  "review.saveVersion": "Enregistrer une nouvelle version",
  "review.changesTitle": p => `Modifications demandées par ${p.actor} le ${p.date}`,
  "review.approvedTitle": p => `Validé par ${p.actor} le ${p.date}`,
  "review.approvedWithFindings": p => `Validé malgré ${p.count} constat(s) bloquant(s), avec la justification ci-dessus.`,
  "review.previousApproved": p => `Une version validée par ${p.actor} le ${p.date} existe, mais le texte actuel est plus récent et doit être validé à nouveau.`,
  "review.restore": "Restaurer la version validée",
  "review.restoreError": "La version validée n'a pas pu être restaurée.",
  "review.carried": "Le texte et les chiffres sont identiques à la version validée : la validation s'applique toujours.",
  "draft.newInputsTitle": "Nouvelles données depuis la dernière exécution de l'agent",
  "draft.newInputsBody": p => `Ajouté(s) ou retraité(s) depuis : ${p.documents}. Relancez l'agent pour les prendre en compte.`,
  "draft.newInputsTag": "Nouvelles données",
  "review.humanVersion": p => `Version humaine ${p.n}`,
  "review.revisionBy": p => `Modifié par ${p.actor} le ${p.date}`,
  "review.regenerateNote": "La nouvelle sortie agent remplacera le texte actuel et devra être validée à nouveau. Les versions précédentes restent dans l'historique.",
  "review.historyTitle": "Versions et décisions",
  "review.runsTitle": "Exécutions agent",
  "review.history.revision": p => `Version ${p.n} enregistrée`,
  "review.history.blocking": p => `${p.count} constat(s) bloquant(s)`,
  "review.history.approved": "Section validée",
  "review.history.changes_requested": "Modifications demandées",
  "review.onRevision": "sur une version modifiée",
  "review.onDraft": "sur la sortie agent",
  "validation.onRevision": p => `Contrôles recalculés sur la version modifiée ${p.n}.`,
  "overview.reviewSub": "sections validées",
  "audit.action.section.edited": "Texte de section modifié",
  "audit.action.section.approved": "Section validée",
  "audit.action.section.changes_requested": "Modifications demandées",
  "audit.category.review": "Revue",
  "draft.confirmTitle": p => `Relancer l'agent sur la section ${p.code} ?`,
  "draft.confirmBody": "Une nouvelle sortie agent sera produite à partir des données actuelles. La version précédente reste consultable dans l'historique.",
  "draft.historyEmpty": "L'agent n'a pas encore été lancé.",
  "draft.runTitle": p => `Exécution de l'agent ${p.code}`,
  "evidence.title": "Preuves",
  "evidence.statement": "Affirmation",
  "evidence.sources": "Sources",
  "evidence.document": "Document",
  "evidence.location": "Emplacement",
  "evidence.openSource": "Ouvrir la source",
  "validation.findings": "Constats",
  "validation.noFindings": "Aucun constat bloquant.",
  "validation.checks": "Contrôles effectués",
  "validation.blocking": "Bloquant",
  "validation.warning": "Avertissement",
  "validation.pass": "Conforme",
  "validation.fail": "Non conforme",
  "validation.emptyTitle": "Aucun contrôle pour le moment",
  "validation.emptyBody": "Les contrôles s'exécutent à chaque exécution agent de la section.",
  "validation.lexicalNote": "Les contrôles d'articles vérifient que chaque élément requis est mentionné ; ils ne jugent pas le contenu, qui reste soumis à la revue humaine.",
  "check.citations": "Chaque renvoi cite une cellule source existante",
  "check.ratios": "Ratios calculés par le serveur, non par le modèle",
  "check.reconciliation": "Best estimate et marge pour risque égalent le total déclaré en S.17.01",
  "check.art308b": "Article 308 bis : best estimate, marge pour risque, hypothèses, courbe des taux",
  "overview.generationSub": "sections produites",
  "chapter.lead": "Lancez l'agent sur tout le chapitre : chaque section est produite à partir de ses propres sources, puis l'introduction du chapitre à partir des sorties agent des sections.",
  "chapter.generate": "Lancer l'agent sur le chapitre",
  "chapter.running": "Agent en cours sur le chapitre",
  "chapter.runningBody": p => `L'agent traite les sections du chapitre ${p.code} en parallèle, puis l'introduction. Temps écoulé : ${p.duration}. Vous pouvez poursuivre votre travail.`,
  "chapter.failed": "L'agent du chapitre a échoué",
  "chapter.sections": "Sections du chapitre",
  "chapter.lastRun": p => `Dernière exécution : ${p.date}, ${p.duration}`,
  "chapter.introduction": "Introduction du chapitre",
  "chapter.notFound": "Chapitre introuvable",
  "draft.passages": "Passages sources",
  "draft.kind.quantitative": "Section chiffrée : cellules QRT et passages",
  "draft.kind.narrative": "Section narrative : rapport précédent et documents d'appui",
  "draft.kind.absence": "Section « Néant » : confirmation à partir du rapport précédent",
  "draft.kind.chapter_lead": "Introduction : sorties agent des sections du chapitre",
  "evidence.passage": "Passage",
  "check.figures": "Chaque chiffre du texte provient d'une source ou d'un calcul du serveur",
  "check.art295": "Article 295 : résultats des placements par catégorie d'actifs",
  "check.balanceSheet": "Total actif moins total passif égale l'excédent d'actif sur passif (S.02.01)",
  "check.solvency": "Ratios de couverture du SCR et du MCR d'au moins 100 %",
  "ind.investissements": "Placements",
  "ind.obligations": "Obligations",
  "ind.fonds_investissement": "Organismes de placement collectif",
  "ind.recouvrables_bilan": "Recouvrables de réassurance (bilan)",
  "ind.tresorerie": "Trésorerie",
  "ind.total_actifs": "Total de l'actif",
  "ind.provisions_techniques_non_vie": "Provisions techniques non-vie",
  "ind.provisions_techniques_vie": "Provisions techniques vie",
  "ind.total_passifs": "Total du passif",
  "ind.excedent_actifs_passifs": "Excédent d'actif sur passif",
  "ind.fonds_propres_base": "Fonds propres de base",
  "ind.fonds_propres_eligibles_scr": "Fonds propres éligibles (SCR)",
  "ind.fonds_propres_eligibles_mcr": "Fonds propres éligibles (MCR)",
  "ind.scr": "SCR",
  "ind.mcr": "MCR",
  "ind.scr_marche": "SCR marché",
  "ind.scr_contrepartie": "SCR contrepartie",
  "ind.scr_vie": "SCR vie",
  "ind.scr_sante": "SCR santé",
  "ind.scr_non_vie": "SCR non-vie",
  "ind.scr_operationnel": "SCR opérationnel",
  "ind.ratio_couverture_scr": "Ratio de couverture du SCR",
  "ind.ratio_couverture_mcr": "Ratio de couverture du MCR",
};

const EN: Dict = {
  "app.name": "SFCR Workspace",
  "app.reportingYear": p => `Reporting year ${p.year}`,
  "app.processing": p => `${p.count} document(s) processing`,
  "app.allProcessed": "No processing in progress",
  "app.prototype": "Prototype",
  "nav.overview": "Overview", "nav.dashboard": "Dashboard", "nav.dossier": "Dossier",
  "nav.documents": "Input documents", "nav.readiness": "Input readiness", "nav.sfcr": "SFCR",
  "nav.chapter": p => `Chapter ${p.code}`, "nav.review": "Review", "nav.issues": "Issues",
  "nav.reviewApproval": "Review & approval", "nav.output": "Output", "nav.report": "Generated report",
  "nav.export": "Export", "nav.system": "System", "nav.history": "Processing history", "nav.audit": "Audit trail",
  "nav.admin": "Administration", "nav.collapse": "Collapse menu", "nav.expand": "Expand menu",
  "nav.switchDossier": "Switch dossier", "nav.notConnected": "Not connected",
  "nav.dossiers": "Dossiers", "nav.current": "Open dossier", "nav.newDossier": "New dossier", "nav.allDossiers": "All dossiers",
  "cmd.open": "Search", "cmd.label": "Search for a page or an action", "cmd.placeholder": "Page, section or action…",
  "cmd.empty": "No results.", "cmd.actions": "Actions", "cmd.pages": "Pages", "cmd.sections": "Chapters and sections",
  "cmd.language": "Language: switch to French (Français)", "cmd.move": "Move", "cmd.select": "Open",
  "status.not_started": "Not started", "status.processing": "Processing", "status.ready": "Ready",
  "status.partial": "Partially ready", "status.missing": "Missing", "status.generated": "Generated",
  "status.needs_review": "Needs review", "status.approved": "Approved", "status.failed": "Failed",
  "status.pending": "Pending",
  "type.ACTUARIAL_REPORT": "Actuarial report", "type.FINANCIAL_REPORT": "Financial report",
  "type.ORG_CHART": "Organizational chart", "type.GROUP_TEMPLATE": "Group template",
  "type.ORSA": "ORSA report", "type.POLICY": "Written policy",
  "type.SUPPORTING_DOCUMENT": "Supporting document", "type.UNKNOWN": "Unidentified",
  "role.N": "Current year", "role.N-1": "N-1", "role.other": "Other period", "role.unknown": "Unknown period",
  "stage.upload": "Upload", "stage.validate": "File validation", "stage.hash": "Hash & duplicates",
  "stage.store": "Storage", "stage.parse": "Parsing", "stage.metadata": "Metadata",
  "stage.classify": "Classification", "stage.period": "Period & entity", "stage.structure": "Structure",
  "stage.qrt": "QRT extraction", "stage.extract": "Data point extraction (text)", "stage.index": "Evidence indexing",
  "common.retry": "Retry", "common.cancel": "Cancel", "common.close": "Close", "common.save": "Save",
  "common.remove": "Remove", "common.restore": "Restore", "common.open": "Open", "common.inspect": "Inspect", "common.loading": "Loading",
  "common.requestId": p => `Reference: ${p.id}`,
  "common.networkError": "The server cannot be reached. Check the connection and retry.",
  "common.genericError": "The request failed.", "common.pages": p => `p. ${p.pages}`, "common.notAvailable": "N/A",
  "readiness.READY": "Inputs complete", "readiness.PARTIALLY_READY": "Inputs partial",
  "readiness.NOT_READY": "Required inputs missing",
  "boundary.title": "This screen could not be displayed",
  "boundary.body": "The dossier data is unaffected. Retry or open another page.",
  "nav.chapterTitle": p => `Chapter ${p.code}: ${p.title}`, "upload.withReason": p => `${p.state}: ${p.reason}`,
  "docs.newVersionOf": p => `New version of ${p.name}`,
  "home.title": "Create an SFCR dossier",
  "home.lead": "A dossier groups the source documents, input readiness and report production for one entity and reporting year.",
  "home.name": "Dossier name", "home.entity": "Entity", "home.year": "Reporting year", "home.type": "Report type",
  "home.create": "Create dossier", "home.select": "Existing dossiers",
  "home.delete": "Delete dossier", "home.deleteTitle": p => `Delete "${p.name}"?`,
  "home.deleteBody": p => `The ${p.entity} (${p.year}) dossier leaves the lists. Its documents, drafts, approvals and audit trail are kept: it can be restored.`,
  "home.deleteConfirm": p => `Type "${p.name}" to confirm`, "home.deletedTitle": "Deleted dossiers",
  "home.deletedHint": "Restorable with all their content",
  "home.deletedOn": p => `deleted on ${p.date}${p.actor ? ` by ${p.actor}` : ""}`, "home.restore": "Restore",
  "overview.period": p => `Reporting period: ${p.year}`, "overview.inputs": "Inputs",
  "overview.inputsSub": "documents ready", "overview.processing": "Processing",
  "overview.processingSub": "documents processed", "overview.generation": "Generation", "overview.review": "Review",
  "overview.notConnected": "Not connected yet", "overview.readiness": "Input readiness",
  "overview.chapters": "Chapter progress", "overview.attention": "Attention required",
  "next.eyebrow": "Next step", "next.import": "Import the dossier's documents",
  "next.importBody": "Current-year QRTs, N-1 SFCR/RSR, actuarial report… Data is extracted at import and sorted by chapter.",
  "next.processing": p => `Processing ${p.count} document(s)`,
  "next.processingBody": "Their data will appear in the chapters once processing ends. You can keep working.",
  "next.check": p => `${p.count} document(s) to check`, "next.checkAction": "Check documents",
  "next.checkBody": "Confirm or correct them so their data fully counts.",
  "next.draft": p => `Run the agent on chapter ${p.code}: ${p.title}`, "next.draftAction": "Open chapter",
  "next.draftBody": p => `${p.count} section(s) have the data they need and are not drafted yet.`,
  "next.review": "Review and approve the drafts", "next.reviewAction": "Open report",
  "next.reviewBody": p => `${p.approved} of ${p.total} section(s) approved.`,
  "next.missing": p => `Data is missing for ${p.count} section(s)`, "next.missingAction": "See what is missing",
  "next.missingBody": "Add the documents that contain it to run the agent.",
  "next.export": "The report is ready to export", "next.exportBody": "Every agent output is approved.",
  "next.exportAction": "Export to Word",
  "overview.attentionCount": p => `${p.count} item(s) require attention`,
  "overview.allClear": "Nothing blocking right now.", "overview.openReadiness": "View details",
  "docs.title": "Input documents",
  "docs.lead": "Manage the source documents used to prepare the report: QRTs, N-1 reports, actuarial reports and supporting documents.",
  "docs.add": "Add documents",
  "reason.entity": p => `This document seems to concern another company (${p.entity}): its figures are not used. If that is wrong, correct the entity.`,
  "reason.qrtRows": p => `${p.count} QRT row(s) could not be read: those values are not used, the others are.`,
  "reason.qrt": "No QRT value could be read in this file.",
  "reason.entityUnknown": "Company not identified in this document: confirm its entity to use its data.",
  "draft.unconfirmed": p => `${p.count} document(s) are not used until their company is confirmed ("Correct" in the document details).`,
  "reason.classification": "Document type uncertain: confirm it.",
  "reason.period": "Reporting year not detected: enter it.",
  "reason.ocr": "Scanned document: part of the text could not be read.", "docs.filter.all": "All", "docs.filter.reports": "SFCR / RSR",
  "docs.filter.actuarial": "Actuarial", "docs.filter.financial": "Financial", "docs.filter.other": "Other",
  "docs.filter.attention": "Needs action", "docs.col.document": "Document", "docs.col.type": "Type",
  "docs.col.period": "Period", "docs.col.processing": "Processing", "docs.col.usedBy": "Used by",
  "docs.col.actions": "Actions", "docs.empty.title": "No source documents yet",
  "docs.empty.body": "Add QRTs, the previous SFCR and supporting documents to start preparing this dossier.",
  "docs.emptyFilter": "No document matches this filter.", "docs.loadError": "The documents could not be loaded.",
  "docs.dossierMissingNote": "This dossier does not exist in the active database. It may have been created in another environment, deleted, or lost before persistent storage was configured.",
  "docs.dossierMissingAction": "Back to dossiers",
  "docs.corrected": "Corrected by a user", "docs.confidence": p => `confidence ${p.value}`,
  "docs.reprocess": "Retry processing", "docs.newVersion": "New version",
  "docs.removeConfirmTitle": "Remove this document from the dossier?",
  "docs.removeConfirmBody": "It will no longer be used for preparation. Its versions and evidence are kept for the audit trail.",
  "docs.removed": "Document removed from the dossier.", "docs.restored": "Document restored to the dossier.",
  "docs.restoreError": "The document could not be restored.", "docs.failedShort": "Processing failed",
  "upload.title": "Add source documents", "upload.drop": "Drop documents here",
  "upload.formats": "Accepted formats: PDF, DOCX, XLSX and PPTX. Up to 25 MB per file.", "upload.browse": "Browse files",
  "upload.hint": "Type, reporting year and entity are detected from the content. You can correct them.",
  "upload.state.uploading": p => `Uploading ${p.percent}%`, "upload.state.accepted": "Received, processing",
  "upload.state.duplicate": "Already imported", "upload.state.rejected": "Rejected",
  "upload.state.cancelled": "Upload cancelled", "upload.state.error": "Upload failed", "upload.viewExisting": "View existing document",
  "upload.continue": "You can close this window: processing continues in the background.", "upload.done": "Done",
  "upload.summary": p => `${p.done}/${p.total} document(s) settled`,
  "upload.kpi.processing": p => `${p.count} processing`, "upload.kpi.ready": p => `${p.count} ready`,
  "upload.kpi.review": p => `${p.count} to review`, "upload.kpi.duplicates": p => `${p.count} duplicate(s)`,
  "upload.kpi.failed": p => `${p.count} failed`, "upload.retryFailed": "Retry failed", "upload.cancelActive": "Cancel uploads",
  "panel.metadata": "Detected metadata", "panel.type": "Document type", "panel.year": "Reporting year",
  "panel.role": "Role", "panel.entity": "Entity", "panel.qrtUnit": "QRT unit", "panel.qrtUnitAsStated": "As stated in the document",
  "panel.qrtUnit.EUR": "Euros (EUR)", "panel.qrtUnit.kEUR": "Thousands of euros (kEUR)", "panel.qrtUnit.MEUR": "Millions of euros (MEUR)",
  "panel.qrtUnitHint": "Correct it when the table's label is wrong (\"in EUR 000\" over amounts in euros). Applies at once and on reprocessing.", "panel.signals": "Classification signals", "panel.correct": "Correct",
  "panel.correctTitle": "Correct metadata",
  "panel.correctHint": "Your correction replaces detection, is recorded in the audit trail and is kept on reprocessing.",
  "panel.processing": "Processing", "panel.review": "To check", "panel.content": "Extracted content",
  "panel.dataPoints": "Extracted data points",
  "panel.sections": "sections", "panel.tables": "tables", "panel.qrtFacts": "QRT cells", "panel.evidence": "evidence items",
  "panel.templates": "QRT templates", "panel.versions": "Versions", "panel.version": p => `Version ${p.n}`,
  "panel.openOriginal": "Open original", "panel.warnings": "Warnings",
  "panel.failedTitle": p => `${p.stage} failed`, "panel.failedOthers": "Other dossier documents are unaffected.",
  "panel.attempt": p => `Attempt ${p.n}`,
  "ready.title": "Input readiness",
  "ready.lead": "For each section: is there enough evidence to draft it?",
  "ready.inProgress": p => `${p.count} document(s) still processing: results may change.`,
  "ready.required": "Required", "ready.optional": "Optional", "ready.viaExtracted": "figures extracted from text (no QRT)", "ready.state.satisfied": "Available",
  "ready.state.pending": "Awaiting review", "ready.state.missing": "Missing", "ready.addDocument": "Add document",
  "ready.reviewDocument": "Review document", "ready.sources": "Available sources",
  "ready.loadError": "Input readiness could not be evaluated.",
  "section.tab.inputs": "Inputs", "section.tab.sources": "Sources", "section.tab.draft": "AI agent",
  "section.tab.validation": "Validation", "section.tab.history": "History", "section.sidebar": "Section control",
  "section.requirementsReady": "Inputs ready", "section.requirementsRequired": "Required", "section.previousReport": "Previous report",
  "section.noPrevious": "No matching section in a previous report of this dossier.", "section.qrt": "QRT templates",
  "section.noQrt": "No required QRT template is available for this section.",
  "section.facts": p => `${p.count} cells`, "section.showFacts": "Show cells", "section.hideFacts": "Hide",
  "section.gapTitle": "Agent not connected",
  "section.gapBody": "Agent execution, the evidence panel and validation for this section need the generation endpoints, which the backend does not expose yet. The prototype production workspace remains available, on demo data.",
  "section.openPrototype": "Open the production workspace (prototype)", "section.truncated": "Excerpt truncated.",
  "issues.title": "Issues", "issues.lead": "One queue of what must be handled before agent execution and approval.",
  "issues.col.severity": "Severity", "issues.col.scope": "Scope", "issues.col.issue": "Issue",
  "issues.col.action": "Action", "issues.severity.high": "High", "issues.severity.medium": "Medium",
  "issues.severity.low": "Low", "issues.input": "Inputs", "issues.empty": "No issues.",
  "issues.docFailed": p => `Processing of ${p.name} failed: ${p.message}`,
  "issues.docReview": p => `${p.name}: ${p.message}`,
  "issues.sectionMissing": p => `Required input missing: ${p.label}`,
  "issues.sectionPartial": p => `Optional input missing: ${p.label}`,
  "issues.sectionPending": p => `Document awaiting review for: ${p.label}`,
  "history.title": "Processing history", "history.lead": "Processing of this dossier's documents: stages, duration and outcome.",
  "history.empty": "No processing yet.", "history.duration": p => `${p.seconds} s`, "history.details": "Technical details",
  "audit.action.generation.requested": "Section agent run requested", "audit.action.generation.completed": "Section agent run completed",
  "audit.action.generation.failed": "Section agent run failed",
  "audit.action.chapter_generation.requested": "Chapter agent run requested",
  "audit.action.chapter_generation.completed": "Chapter agent run completed",
  "audit.action.chapter_generation.failed": "Chapter agent run failed",
  "audit.filter.category": "Type", "audit.filter.actor": "Actor", "audit.category.all": "All",
  "audit.category.documents": "Documents", "audit.category.drafting": "AI agents", "audit.category.corrections": "Corrections",
  "audit.category.dossier": "Dossier", "audit.actor.all": "All", "audit.actor.user": "Users", "audit.actor.system": "System",
  "audit.count": p => `${p.count} event(s)`, "audit.runSummary": p => `${p.count} sections: ${p.states}`,
  "audit.findings": p => `${p.count} finding(s)`, "audit.gaps": p => `${p.count} missing item(s)`,
  "nav.audits": "Audits", "audits.title": "Report audits",
  "audits.lead": "Three independent audits: figure coherence within the report, regulatory compliance article by article, and consistency across years (N-1 in this report against N published last year).",
  "audits.export": "Export to Excel", "audits.summary": "Checks summary", "audits.kpi.total": "Total checks",
  "audits.kpi.ok": "OK", "audits.kpi.ko": "KO", "audits.kpi.review": "To review", "audits.kpi.na": "N/A",
  "audits.basis": p => `Computed on ${p.count} agent-produced section(s), ${p.at}.`,
  "audits.tab.coherence": "Figure coherence", "audits.tab.regulatory": "Regulatory", "audits.tab.vintage": "Across years",
  "audits.koCount": p => `${p.count} KO`, "audits.result.ok": "OK", "audits.result.ko": "KO", "audits.result.review": "To review",
  "audits.result.na": "N/A", "audits.col.result": "Result", "audits.col.aggregate": "Aggregate",
  "audits.col.appearances": "Appearances", "audits.col.comment": "Comment", "audits.col.reference": "Reference",
  "audits.col.section": "Section", "audits.col.requirement": "Requirement", "audits.col.indicator": "Indicator",
  "audits.col.current": "N-1 (this report)", "audits.col.published": "N published (previous report)",
  "audits.col.difference": "Difference", "audits.text": "text", "audits.unitAssumed": "assumed unit", "audits.automated": "Automated check",
  "audits.excerpt": "Passage concerned",
  "audits.noPublished": "No published N-1 SFCR or RSR in the dossier: add it to compare N-1 figures with those published last year.",
  "audits.empty.coherence": "No figure cited in agent outputs yet. Run the agent on sections to run this check.",
  "audits.empty.regulatory": "No regulatory check.", "audits.empty.vintage": "No N-1 data to compare.",
  "audits.emptyFilter": "No check matches this filter.",
  "audits.note": "Live audits: each row opens the section concerned, and running the agent again recomputes the audits. An OK result means no difference was found; human review is still required.",
  "issues.audit": p => `${p.audit} · ${p.what}: ${p.comment}`,
  "issues.auditsNote": "Findings of the report audits (figure coherence, regulatory checks, across years) are included in this queue.",
  "issues.openAudits": "Open the audits",
  "audit.title": "Audit trail", "audit.lead": "Who did what, and when, on this dossier.", "audit.empty": "No events recorded.",
  "audit.action.dossier.created": "Dossier created", "audit.action.document.uploaded": "Document imported",
  "audit.action.document.version_added": "New version imported", "audit.action.document.processed": "Document processed",
  "audit.action.document.processing_failed": "Processing failed",
  "audit.action.document.reprocess_requested": "Reprocessing requested",
  "audit.action.document.metadata_corrected": "Metadata corrected", "audit.action.document.removed": "Document removed",
  "audit.action.document.restored": "Document restored", "audit.system": "System",
  "locale.number": "en-GB",
  "ind.primes_emises": "Gross written premiums", "ind.primes_acquises": "Gross earned premiums", "ind.charge_sinistres": "Net claims incurred",
  "ind.frais_generaux": "Expenses incurred", "ind.ratio_sinistres_primes": "Loss ratio",
  "ind.ratio_frais_primes": "Expense ratio", "ind.best_estimate": "Gross best estimate", "ind.marge_risque": "Risk margin",
  "ind.recouvrables_reassurance": "Reinsurance recoverables", "ind.provisions_techniques": "Reported technical provisions",
  "ind.provisions_techniques_brutes_calculees": "Gross technical provisions (BE and risk margin)",
  "ind.provisions_techniques_nettes_calculees": "Technical provisions net of reinsurance", "ind.best_estimate_vie": "Gross life best estimate", "ind.marge_risque_vie": "Life risk margin",
  "ind.recouvrables_vie": "Life recoverables", "ind.provisions_techniques_vie_total": "Life technical provisions (S.12.01)",
  "ind.provisions_techniques_vie_calculees": "Life technical provisions (BE and risk margin)",
  "ind.provisions_techniques_vie_nettes_calculees": "Life technical provisions net of reinsurance",
  "ind.variation": p => `Change: ${p.label}`,
  "confidence.eleve": "High confidence", "confidence.moyen": "Medium confidence", "confidence.faible": "Low confidence",
  "draft.agentInputs": "Inputs given to the agent", "draft.canDraft": "Ready for the agent", "draft.cannotDraft": "Required inputs missing",
  "draft.col.indicator": "Indicator", "draft.col.year": "Year", "draft.col.value": "Value", "draft.col.source": "Source",
  "draft.col.confidence": "Reliability", "draft.col.formula": "Calculation",
  "draft.derivedFromCells": "Calculated from two template cells", "draft.missingInputs": "Unavailable inputs",
  "draft.inputsError": "The section inputs could not be loaded.", "draft.loadError": "The section agent runs could not be loaded.",
  "draft.startError": "The agent could not be started.", "draft.unsupportedTitle": "AI agent not available",
  "draft.unsupportedBody": "This section is not part of chapters A to E covered by the AI agent.",
  "draft.state.none": "Agent not run", "draft.state.running": "Agent running", "draft.state.failed": "Agent failed",
  "draft.state.produced": "Agent output to review", "draft.state.audit_ko": "Blocking finding",
  "draft.state.escalated": "Required inputs missing", "draft.state.empty": "Agent not run",
  "draft.aiGenerated": "Agent output, not reviewed", "draft.generate": "Run agent", "draft.regenerate": "Run agent again",
  "draft.generating": "Agent running",
  "draft.runningBody": p => `The agent is analyzing the inputs and producing ${p.code}. Elapsed time: ${p.duration}. You can keep working.`,
  "draft.failedTitle": "The agent run failed", "draft.aiNotConfigured": "The AI provider is not configured on the server.",
  "draft.escalatedTitle": "Agent paused: data is missing",
  "draft.escalatedBody": "The imported documents do not contain the required information (see \"Missing information\"). Add the document that does, then run the agent again.",
  "draft.emptyTitle": "The agent has not run on this section yet",
  "draft.emptyBody": "The agent only uses your imported documents, and every sentence links to its source.",
  "draft.notReadyTitle": "Required data missing",
  "draft.notReadyBody": "Add the required documents first (see \"Inputs\"): without them the agent cannot produce the section.",
  "draft.foreignTitle": "The documents concern another company",
  "draft.foreignAll": p => `The ${p.count} document(s) of this dossier seem to concern a company other than "${p.entity}": their data is not used. If that is wrong, correct each document's entity; otherwise import the documents of "${p.entity}".`,
  "draft.foreignSome": p => `${p.count} document(s) of the dossier are ignored because they seem to concern a company other than "${p.entity}".`,
  "draft.generatedBy": p => `Produced by the agent on ${p.date}`,
  "draft.runDetails": p => `Model ${p.model} · ${p.seconds} · ${p.tokens} tokens · ${p.prompt}`,
  "gap.missingFact": p => `Missing data: ${p.indicator} (${p.year})`,
  "gap.noSource": "No source for this section: add the N-1 report or a supporting document.", "draft.tokens": p => `${p.count} tokens`,
  "draft.citation": p => `View source ${p.n}`, "draft.selectHint": "Select a sentence or a numbered reference to see its evidence.",
  "draft.figure": p => `N-1 report illustration kept as is (${p.id}): check it for the year.`,
  "draft.missingInfo": "Missing information", "draft.missingInfoHint": "What the sources do not establish",
  "draft.derived": "Calculated figures", "draft.derivedHint": "Calculated by the server from source data and quoted as is",
  "rollover.button": "New reporting year",
  "dossierEdit.button": "Edit", "dossierEdit.title": "Edit dossier", "dossierEdit.name": "Dossier name",
  "dossierEdit.entity": "Entity (company name)", "dossierEdit.save": "Save", "dossierEdit.year": "Reporting year",
  "dossierEdit.yearNote": p => `The documents' N / N-1 roles will be recomputed for ${p.year}: for instance the ${Number(p.year) - 1} SFCR becomes the previous report (N-1). A role corrected by hand is kept.`, "dossierEdit.error": "The dossier could not be updated",
  "dossierEdit.entityNote": "Documents will be re-checked: those about this entity are used, the others are flagged as concerning another company. An entity corrected by hand on a document is kept.", "rollover.title": "Prepare the next reporting year",
  "rollover.body": "A new dossier is created for the same entity. The team and RACI matrix are carried over, and this report's approved sections are imported as the N-1 SFCR: next year's agents start from this approved text. Sections not approved are not carried over.",
  "rollover.year": "Reporting year", "rollover.name": "Dossier name", "rollover.create": "Create the dossier",
  "rollover.error": "The dossier could not be created.", "audit.action.dossier.rolled_over": "New reporting year created",
  "nav.planning": "Planning", "planning.title": "Planning and deadlines",
  "planning.lead": "Filing date, deadlines by chapter or section, the owner of each block from the RACI, and what is late.",
  "planning.summary": "Planning summary", "planning.filing": "Filing date", "planning.noDate": "Not set",
  "planning.noFiling": "Set the filing date: chapters and sections without their own deadline inherit it.",
  "planning.kpi.approved": "Blocks approved", "planning.kpi.late": "Late", "planning.kpi.due_soon": "Due within 7 days",
  "planning.kpi.without_owner": "Without owner", "planning.filter.all": "All blocks", "planning.filter.late": "Late",
  "planning.filter.soon": "Within 7 days", "planning.filter.open": "Not approved", "planning.owner": "Owner (RACI)",
  "planning.due": "Deadline", "planning.delay": "Delay", "planning.status": "Review",
  "planning.dueFor": p => `Deadline of ${p.code}`, "planning.inherited.chapter": p => `from the chapter: ${p.date}`,
  "planning.inherited.filing": p => `filing date: ${p.date}`, "planning.noOwner": "To assign in Team and RACI",
  "planning.late": p => `${p.days} d late`, "planning.soon": p => Number(p.days) === 0 ? "Today" : `In ${p.days} d`,
  "planning.done": "Approved",
  "planning.note": "A block is late when its deadline has passed and it is not approved. The owner is the chapter's R in the RACI matrix. Only approvers change the dates.",
  "audit.action.schedule.deadline_set": "Deadline changed",
  "nav.team": "Team and RACI", "team.title": "Team and RACI",
  "team.lead": "The report's contributors and who is responsible for what. Information requests of a section go to the Responsible (R) of its chapter by default.",
  "team.contributors": "Contributors", "team.count": p => `${p.count} contributor(s)`, "team.empty": "No contributor",
  "team.emptyBody": "Add the people to ask for missing information.", "team.field.name": "Name", "team.field.email": "E-mail",
  "team.field.role": "Function", "team.field.department": "Department", "team.add": "Add",
  "team.addError": "The contributor could not be added.", "team.saveError": "The change could not be saved.",
  "team.status": "Status", "team.active": "Active", "team.inactive": "Inactive", "team.activate": "Reactivate",
  "team.deactivate": "Deactivate", "team.raci": "RACI matrix", "team.raciHint": "By chapter; sections inherit their chapter.",
  "team.scope": "Chapter", "team.role.R": "Responsible", "team.role.A": "Accountable", "team.role.C": "Consulted",
  "team.role.I": "Informed", "team.nobody": "Nobody", "team.addPerson": "Add…", "team.remove": p => `Remove ${p.name}`,
  "team.note": "When e-mail sending is configured (SMTP), the request is sent automatically; otherwise the platform gives the answer link to pass on (copy or prepared e-mail).",
  "request.ask": "Ask a contributor", "request.new": "New request", "request.title": p => `Ask for information for ${p.code}`,
  "request.template": p => `For section ${p.code} of the report, could you provide the following information, with its source: ${p.gap}`,
  "request.to": "Recipient", "request.choose": "Choose a contributor", "request.responsible": "chapter Responsible",
  "request.noTeam": "No contributor: add some in Team and RACI.", "request.question": "Question", "request.create": "Create the request",
  "request.createError": "The request could not be created.", "request.createdTitle": p => `Request created for ${p.name}`,
  "request.createdBody": "Pass on this link: it is shown only once. The answer will become a source of the section.",
  "request.emailed": p => `E-mail sent to ${p.email}. The link is also shown below, once.`, "request.emailFailed": "The e-mail could not be sent: pass the link on yourself.", "request.link": "Answer link", "request.copy": "Copy the link", "request.copied": "Link copied", "request.mail": "Prepare the e-mail",
  "request.mailSubject": p => `SFCR ${p.entity}: information requested for section ${p.code}`,
  "request.mailBody": p => `Hello ${p.name},\n\n${p.question}\n\nYou can answer directly here: ${p.link}\n\nThank you.`,
  "request.listTitle": "Requests to contributors", "request.none": "No request for this section.",
  "request.meta": p => `To ${p.name}, on ${p.date}`, "request.status.open": "Waiting", "request.status.answered": "Answered",
  "request.status.cancelled": "Withdrawn", "request.cancel": "Withdraw",
  "request.answeredNote": "Answers are sources of the section: run the agent again so it cites them.",
  "contribute.app": "SFCR platform", "contribute.invalid": "This link is not valid.", "contribute.title": p => `Hello ${p.name}`,
  "contribute.context": p => `Information is requested for section ${p.code} "${p.title}", by ${p.asker} on ${p.date}.`,
  "contribute.cancelled": "This request was withdrawn: no answer is expected.", "contribute.thanks": "Thank you, your answer was sent.",
  "contribute.alreadyAnswered": "Answer already sent", "contribute.answer": "Your answer",
  "contribute.hint": "State the source of the information (document, date, exact figure). Your answer will be cited in the report.",
  "contribute.sendError": "The answer could not be sent.", "contribute.send": "Send the answer",
  "audit.action.team.contributor_added": "Contributor added", "audit.action.team.contributor_activated": "Contributor reactivated",
  "audit.action.team.contributor_deactivated": "Contributor deactivated", "audit.action.team.raci_updated": "RACI updated",
  "audit.action.request.created": "Information requested from a contributor", "audit.action.request.answered": "Contributor answer",
  "audit.action.request.cancelled": "Request withdrawn",
  "nav.plan": "Report plan", "plan.eyebrow": "Before production", "plan.title": "Report plan to validate",
  "plan.lead": "The architect compares the report to produce with the N-1 SFCR: section mapping, conflicts to arbitrate and proposed changes, before any drafting.",
  "plan.validate": "Validate the plan", "plan.saveError": "The decision could not be saved.",
  "plan.validatedTitle": p => `Plan validated by ${p.actor} on ${p.date}`,
  "plan.validatedBody": "The decisions apply to the sources the agents read.",
  "plan.outdatedTitle": "The plan changed since it was validated",
  "plan.outdatedBody": "New documents changed the conflicts or proposals. Arbitrate the new items, then validate again.",
  "plan.pendingBody": p => `${p.count} item(s) to arbitrate before validating the plan.`,
  "plan.noPrevious": "No N-1 SFCR or RSR in the dossier: the plan cannot follow last year's structure.",
  "plan.noPreviousShort": "No N-1 report", "plan.summary": "Plan summary", "plan.kpi.blocks": "report blocks",
  "plan.kpi.kinds": "typologies", "plan.kpi.conflicts": "conflicts to arbitrate", "plan.kpi.proposals": "proposed changes",
  "plan.tree": "Proposed structure", "plan.col.block": "Block", "plan.col.kind": "Typology", "plan.col.previous": "N-1 section",
  "plan.kind.quantitative": "Figures and interpretation", "plan.kind.narrative": "Narrative",
  "plan.kind.absence": "Statement (none or change)", "plan.kind.chapter_lead": "Chapter introduction", "plan.kind.synthesis": "Summary",
  "plan.mapping.renumbered": "Different numbering", "plan.mapping.title_differs": "Different title",
  "plan.mapping.absent": "Not in N-1", "plan.mapping.none": "No N-1", "plan.items": "Conflicts and proposals",
  "plan.filter.pending": "To arbitrate", "plan.filter.all": "All", "plan.nothingPending": "Every item is arbitrated.",
  "plan.noItems": "No conflict or proposal.", "plan.severity.high": "Conflict", "plan.severity.medium": "To check",
  "plan.severity.low": "Proposal", "plan.acknowledge": "Acknowledged", "plan.accept": "Accept", "plan.reject": "Reject",
  "plan.decided.accepted": p => `Accepted by ${p.actor} on ${p.date}`, "plan.decided.rejected": p => `Rejected by ${p.actor} on ${p.date}`,
  "plan.note": "Rejecting a mapping removes that N-1 section from the agent's sources; accepting a section without equivalent adds it to the proposed section's sources. Drafting remains possible before the plan is validated.",
  "audit.action.plan.item_accepted": "Plan item accepted", "audit.action.plan.item_rejected": "Plan item rejected",
  "audit.action.plan.validated": "Report plan validated",
  "nav.users": "Users", "auth.needs": p => `Reserved to the ${p.role} role or above.`,
  "users.title": "Users", "users.lead": "Platform accounts and their role. A reviewer never approves a version they edited.",
  "users.offTitle": "Sign-in disabled",
  "users.offBody": "The platform runs without sign-in (SFCR_AUTH_MODE=off). Set SFCR_AUTH_MODE=local to manage accounts.",
  "users.adminOnly": "Reserved to administrators.", "users.list": "Accounts", "users.empty": "No account.", "users.role": "Role",
  "users.password": "Initial password", "users.passwordHint": "At least 12 characters. Pass it on through a safe channel.",
  "users.note": "Deactivating an account closes its open sessions at once. Every change is recorded.",
  "password.menu": "Password", "password.title": "Change password", "password.current": "Current password",
  "password.new": "New password", "password.again": "Confirm the new password", "password.mismatch": "The two entries differ.",
  "password.hint": "At least 12 characters. Your other open sessions will be closed.", "password.save": "Save",
  "password.done": "Password changed.", "password.error": "The password could not be changed.",
  "password.reset": "Reset password", "password.resetTitle": p => `Reset the password of ${p.name}`,
  "password.resetHint": "At least 12 characters. Pass it on through a safe channel; the person's open sessions are closed.",
  "password.resetDone": "Password reset.", "audit.action.auth.password_changed": "Password changed",
  "audit.action.auth.password_reset": "Password reset",
  "login.title": "Sign in", "login.lead": "Sign in to access the SFCR platform.", "login.email": "E-mail",
  "login.password": "Password", "login.submit": "Sign in", "login.error": "Sign-in failed.",
  "login.note": "Sessions expire after 8 hours. Accounts are created by an administrator.", "login.signOut": "Sign out",
  "role.user.preparer": "Preparer", "role.user.reviewer": "Reviewer", "role.user.approver": "Approver", "role.user.admin": "Administrator",
  "audit.action.auth.login": "Signed in", "audit.action.auth.login_failed": "Sign-in failed",
  "audit.action.auth.user_created": "User created", "audit.action.auth.user_updated": "User updated",
  "refv.default": "Original version", "refv.version": p => `Version ${p.n}`, "refv.propose": "Propose a change",
  "refv.proposeTitle": p => `Propose a change: ${p.label}`, "refv.newCriteria": "New validation criteria",
  "refv.newInstructions": "New drafting instructions", "refv.reason": "Reason for the change (required)",
  "refv.proposeNote": "The change applies only once an administrator approves it; its author cannot approve it.",
  "refv.submit": "Propose", "refv.error": "The action could not be saved.", "refv.history": "History",
  "refv.noHistory": "No change: the original version is in force.",
  "refv.proposedBy": p => `Proposed by ${p.actor} on ${p.date}`, "refv.decidedBy": p => `decided by ${p.actor} on ${p.date}`,
  "refv.status.proposed": "Waiting", "refv.status.approved": "Approved", "refv.status.rejected": "Rejected",
  "refv.pendingTitle": p => `Version ${p.n} proposed by ${p.actor}`, "refv.pendingCount": p => `${p.count} waiting`,
  "refv.approve": "Approve", "refv.reject": "Reject", "refv.confirmReject": "Confirm the rejection",
  "refv.rejectReason": "Reason for the rejection (required)", "refv.waitingAdmin": "Waiting for an administrator's approval.",
  "audit.action.referential.proposed": "Referential change proposed", "audit.action.referential.approved": "Referential change approved",
  "audit.action.referential.rejected": "Referential change rejected",
  "nav.referential": "Referential", "ref.eyebrow": "Administration", "ref.title": "Agents' referential",
  "ref.lead": "What the agents draft and check with: articles of the Delegated Regulation and their criteria, section catalogue, drafting configuration.",
  "ref.tab.articles": "Regulatory articles", "ref.tab.sections": "Section catalogue", "ref.tab.drafting": "Agents and rules",
  "ref.col.reference": "Reference", "ref.col.title": "Article", "ref.col.criteria": "Validation criteria", "ref.col.sections": "Sections",
  "ref.col.control": "Check", "ref.col.block": "Block", "ref.col.kind": "Typology", "ref.col.instructions": "Drafting instructions",
  "ref.col.inputs": "Data", "ref.col.checks": "Checks", "ref.automated": "Automated", "ref.manual": "Human review",
  "ref.required": "Required:", "ref.config": "Drafting configuration", "ref.configured": "Configured", "ref.notConfigured": "Not configured",
  "ref.mode": "Provider", "ref.deployment": "Model (deployment)", "ref.promptVersion": "Prompt version", "ref.guardrails": "Checks applied",
  "ref.guardrail.citations": "verified citations", "ref.guardrail.figures": "sourced figures", "ref.guardrail.markers": "aligned markers",
  "ref.guardrail.articles": "articles", "ref.guardrail.identities": "QRT identities", "ref.guardrail.style": "N-1 length",
  "ref.guardrail.changes": "explained changes", "ref.rule": p => `Rule: ${p.kind}`,
  "ref.note": "Changing a criterion or an instruction changes what the agents write and check: each change is a proposed version, applied only once approved, and each run records the versions it used.",
  "draft.layout.label": "Layout", "draft.layout.side": "Checks on the right", "draft.layout.stack": "Top-down layout",
  "draft.insights": "To check", "draft.findingsTitle": "Checks",
  "draft.findingsCount": p => `${p.blocking} blocking, ${p.warnings} to check`, "draft.noFindings": "No finding on this text",
  "chain.title": "Production chain", "chain.summary": p => `${p.done} of ${p.total} step(s) done`, "chain.human": "human",
  "chain.step.extract": "QRT figure extraction", "chain.step.compute": "Server calculations", "chain.step.prechecks": "Data checks",
  "chain.step.sources": "Source retrieval", "chain.step.draft": "Agent drafting", "chain.step.citations": "Citation verification",
  "chain.step.figures": "Figure and marker checks", "chain.step.regulatory": "Regulatory checks",
  "chain.step.escalation": "Information requested", "chain.step.review": "Review and approval",
  "chain.state.done": "Done", "chain.state.ko": "Blocking", "chain.state.warning": "To check", "chain.state.skipped": "Not applicable",
  "chain.state.pending": "Waiting", "chain.state.running": "Running",
  "chain.extractDetail": p => `${p.facts} QRT cell(s) used${Number(p.missing) ? `, ${p.missing} missing` : ""}.`,
  "chain.noFacts": "No QRT cell for this section.", "chain.computeDetail": p => `${p.count} computed figure(s): ratios, totals, changes.`,
  "chain.noFinding": "No finding.",
  "chain.sourcesDetail": p => `${p.count} passage(s): N-1 report, supporting documents${Number(p.answers) ? `, ${p.answers} contributor answer(s)` : ""}.`,
  "chain.escalated": "Drafting paused: required inputs missing.", "chain.draftDetail": p => `Draft produced by ${p.model}.`,
  "chain.citationsDetail": p => `${p.count} citation(s) verified against the sources.`,
  "chain.requestsDetail": p => `${p.open} request(s) waiting, ${p.answered} answered.`,
  "chain.gapsDetail": p => `${p.count} missing item(s), no request sent.`, "chain.noGap": "No missing information.",
  "section.tab.comparison": "N-1 comparison", "lang.fr": "French", "lang.en": "English", "compare.noPrevious": "No N-1 section to compare",
  "compare.noPreviousBody": "The dossier holds no N-1 SFCR or RSR of this entity for this section, or the plan rejected the mapping.",
  "compare.source": p => `${p.name} · ${p.sections}`, "compare.changed": p => `${p.share} % of the text changed`,
  "compare.noCurrent": "The section is not drafted yet: only the N-1 text is shown.", "compare.noCurrentShort": "Section not drafted.",
  "compare.languages": p => `The two texts are not in the same language (N-1: ${p.before}, current: ${p.after}): they are shown side by side, without highlighting.`,
  "compare.tooLong": "Texts too long for a word-by-word highlight: they are shown side by side.",
  "compare.diffTitle": "Changes from N-1", "compare.removed": "removed", "compare.added": "added",
  "compare.previousTitle": "N-1 text", "compare.currentTitle": "Current text",
  "table.delta": "Change N/N-1", "table.deltaPct": "Change %", "table.na": "n/a", "table.computed": "computed",
  "table.noPrior": "N-1 not available: add the N-1 QRTs or N-1 SFCR to get the change. Rounded amounts: exact value and source on hover.",
  "nav.datapoints": "Data points", "dp.title": "Data points",
  "dp.lead": "Every indicator extracted at import, from the QRTs or from the text of documents (SFCR/RSR, actuarial report...): N and N-1 values, the change, the source and the chapters and sections using it.",
  "dp.kpi.from_text": "indicators from text", "dp.chapter": "Chapter", "dp.method.qrt": "QRT", "dp.method.text": "Text",
  "dp.chapterTitle": "Chapter data points", "dp.viewAll": "All data points",
  "dp.chapterEmpty": "No data point extracted for this chapter yet. Import the QRTs or a document that states them (N-1 SFCR, actuarial report...).",
  "dp.export": "Export to Excel", "dp.summary": "Data points summary", "dp.kpi.found_n": "N data points",
  "dp.kpi.found_n1": "N-1 data points", "dp.kpi.with_change": "N/N-1 changes computed", "dp.kpi.indicators": "indicators tracked",
  "dp.noPrior": "No N-1 value in the dossier: add the N-1 QRTs or N-1 SFCR to compute the changes.",
  "dp.template": "Template", "dp.datapoint": "Data point (QRT)", "dp.source": "Source", "dp.usedBy": "Used by",
  "dp.notes": "Extraction notes",
  "dp.note": "A QRT cell always wins. Otherwise the value comes from a document's text: the model locates and copies it, the server checks it appears verbatim in the passage and converts it to kEUR. These are the values the agents receive.",
  "check.style": "Length consistent with the N-1 section", "check.markers": "Each marker sits next to the amount of the cell it cites", "table.indicator": "Indicator", "table.variation": "Change", "table.var": "Var.", "table.hoverNote": "Rounded amounts: exact value and sources on hover.", "unit.bn": "bn€", "unit.m": "m€",
  "table.note": "Table built by the server from the QRT cells and computed figures. Rounded amounts: exact value and source on hover.",
  "table.provenance": "Sources and validation",
  "table.conflicting_sources": "Conflicting sources",
  "table.carried_forward": "Previous year: confirm for N",
  "table.pending_review": "To validate",
  "review.staleSources": "Sources have changed. The previous approval is no longer valid.",
  "nav.synthesis": "Summary", "nav.exportHint": "Download the report as a Word document",
  "report.title": "The report",
  "report.referenceTemplate": "Report template",
  "report.referenceTemplateLead": "SFPJ SFCR template with narratives, tables and source references.",
  "report.referenceTemplateNote": "SFPJ 2025 reference template: XXX values remain to be completed. This preview is not populated from dossier data and does not change the generated report.",
  "report.backToReport": "Back to report",
  "report.openTemplate": "Open full screen",
  "report.downloadTemplate": "Download HTML",
  "report.lead": "The report as one document: summary, then chapters A to E, with each section's current text and review status.",
  "report.export": "Export to Word", "report.exportPdf": "Export to PDF", "report.moreExports": "Other formats",
  "report.templateOn": p => `Word export: company template applied (styles, headers and footers), updated on ${p.date}.`,
  "report.templateOff": "Word export: standard layout. Add the company's Word template to use its styles, headers and footers.",
  "report.templateUpload": "Add the Word template", "report.templateReplace": "Replace the template",
  "report.templateRemove": "Remove the template", "report.templateError": "The template could not be saved.", "report.print": "Print / PDF", "report.printStatus": p => `${p.approved} of ${p.total} section(s) approved`, "report.printDraft": "Draft, not approved.", "report.progress": "Report progress", "report.kpi.drafted": "Sections drafted",
  "report.kpi.approved": "Sections approved", "report.kpi.changes": "Changes requested", "report.kpi.export": "Export",
  "report.exportNote": "Sections not approved are marked \"Draft\" and each section lists its sources.",
  "report.filter": "Show", "report.filter.all": "All sections", "report.filter.to_review": "To approve",
  "report.filter.approved": "Approved", "report.filter.missing": "Not drafted",
  "report.approvedBy": p => `Approved by ${p.actor} on ${p.date}`, "report.notDrafted": "Section not drafted.",
  "report.emptyFilter": "No section matches this filter.",
  "report.note": "The report shows each section's current text: the latest human version or, failing that, the agent's latest draft. Select a statement to open the section, its evidence and its review.",
  "synthesis.eyebrow": "Article 292", "synthesis.title": "Summary",
  "synthesis.lead": "The summary at the top of the report, drafted from the introductions of chapters A to E and section E.2. Run the agent on the chapters first.",
  "synthesis.openReport": "View the report", "audit.action.report.exported": "Report exported", "audit.action.report.template_set": "Word template added", "audit.action.report.template_removed": "Word template removed",
  "review.status.none": "Not drafted", "review.status.ai_draft": "Draft to approve", "review.status.edited": "Edited, to approve",
  "review.status.changes_requested": "Changes requested", "review.status.approved": "Approved",
  "review.action.edit": "Edit", "review.action.approved": "Approve", "review.action.changes_requested": "Request changes",
  "review.dialog.approved": "Approve the section", "review.dialog.changes_requested": "Request changes",
  "review.blockingTitle": p => `${p.count} blocking finding(s) on this text`,
  "review.justification": "Justification of the approval (required)", "review.comment": "Changes expected (required)",
  "review.commentOptional": "Comment (optional)", "review.saveError": "Saving failed.",
  "review.loadError": "The section review could not be loaded.", "review.editTitle": "Edit the text",
  "review.editHint": p => Number(p.count) ? `Keep the markers [1] to [${p.count}] next to the statements they support.` : "This draft cites no source.",
  "review.changedNote": "The text changed in the meantime. Reload the section, then edit again.",
  "review.saveVersion": "Save a new version",
  "review.changesTitle": p => `Changes requested by ${p.actor} on ${p.date}`,
  "review.approvedTitle": p => `Approved by ${p.actor} on ${p.date}`,
  "review.approvedWithFindings": p => `Approved despite ${p.count} blocking finding(s), with the justification above.`,
  "review.previousApproved": p => `A version approved by ${p.actor} on ${p.date} exists, but the current text is newer and must be approved again.`,
  "review.restore": "Restore the approved version", "review.restoreError": "The approved version could not be restored.",
  "review.carried": "The text and figures are identical to the approved version: the approval still applies.",
  "draft.newInputsTitle": "New data since the agent's last run",
  "draft.newInputsBody": p => `Added or reprocessed since: ${p.documents}. Run the agent again to use them.`,
  "draft.newInputsTag": "New data",
  "review.humanVersion": p => `Human version ${p.n}`, "review.revisionBy": p => `Edited by ${p.actor} on ${p.date}`,
  "review.regenerateNote": "The new draft will replace the current text and will need a new approval. Earlier versions stay in the history.",
  "review.historyTitle": "Versions and decisions", "review.runsTitle": "Agent runs",
  "review.history.revision": p => `Version ${p.n} saved`, "review.history.blocking": p => `${p.count} blocking finding(s)`,
  "review.history.approved": "Section approved", "review.history.changes_requested": "Changes requested",
  "review.onRevision": "on an edited version", "review.onDraft": "on the AI draft",
  "validation.onRevision": p => `Checks recomputed on edited version ${p.n}.`, "overview.reviewSub": "sections approved",
  "audit.action.section.edited": "Section text edited", "audit.action.section.approved": "Section approved",
  "audit.action.section.changes_requested": "Changes requested", "audit.category.review": "Review",
  "draft.confirmTitle": p => `Run the agent on ${p.code} again?`,
  "draft.confirmBody": "A new version will be produced from the current inputs. The previous version stays available in the history.",
  "draft.historyEmpty": "The agent has not run yet.", "draft.runTitle": p => `${p.code} agent run`,
  "evidence.title": "Evidence", "evidence.statement": "Statement", "evidence.sources": "Sources", "evidence.document": "Document",
  "evidence.location": "Location", "evidence.openSource": "Open source",
  "validation.findings": "Findings", "validation.noFindings": "No blocking finding.", "validation.checks": "Checks performed",
  "validation.blocking": "Blocking", "validation.warning": "Warning", "validation.pass": "Pass", "validation.fail": "Fail",
  "validation.emptyTitle": "No checks yet", "validation.emptyBody": "Checks run every time the section is drafted.",
  "validation.lexicalNote": "Article checks verify that each required item is mentioned; they do not judge the content, which remains subject to human review.",
  "check.citations": "Every reference cites an existing source cell", "check.ratios": "Ratios computed by the server, not the model",
  "check.reconciliation": "Best estimate plus risk margin equals the total reported in S.17.01",
  "check.art308b": "Article 308 bis: best estimate, risk margin, assumptions, rate curve",
  "overview.generationSub": "sections drafted",
  "chapter.lead": "Run the agent on the whole chapter: each section from its own sources, then the chapter introduction from the section drafts.",
  "chapter.generate": "Run chapter agent", "chapter.running": "Chapter agent running",
  "chapter.runningBody": p => `The agent works on the ${p.code} sections in parallel, then the introduction. Elapsed time: ${p.duration}. You can keep working.`,
  "chapter.failed": "Chapter drafting failed", "chapter.sections": "Chapter sections",
  "chapter.lastRun": p => `Last drafted: ${p.date}, ${p.duration}`, "chapter.introduction": "Chapter introduction",
  "chapter.notFound": "Chapter not found", "draft.passages": "Source passages",
  "draft.kind.quantitative": "Quantitative section: QRT cells and passages",
  "draft.kind.narrative": "Narrative section: previous report and supporting documents",
  "draft.kind.absence": "\"Nothing to report\" section: confirmed from the previous report",
  "draft.kind.chapter_lead": "Introduction: the chapter's section drafts", "evidence.passage": "Passage",
  "check.figures": "Every figure in the text comes from a source or a server calculation",
  "check.art295": "Article 295: investment results by asset category",
  "check.balanceSheet": "Total assets minus total liabilities equals the excess of assets (S.02.01)",
  "check.solvency": "SCR and MCR coverage ratios of at least 100 %",
  "ind.investissements": "Investments", "ind.obligations": "Bonds", "ind.fonds_investissement": "Collective investment undertakings",
  "ind.recouvrables_bilan": "Reinsurance recoverables (balance sheet)", "ind.tresorerie": "Cash",
  "ind.total_actifs": "Total assets", "ind.provisions_techniques_non_vie": "Non-life technical provisions",
  "ind.provisions_techniques_vie": "Life technical provisions", "ind.total_passifs": "Total liabilities",
  "ind.excedent_actifs_passifs": "Excess of assets over liabilities", "ind.fonds_propres_base": "Basic own funds",
  "ind.fonds_propres_eligibles_scr": "Eligible own funds (SCR)", "ind.fonds_propres_eligibles_mcr": "Eligible own funds (MCR)",
  "ind.scr": "SCR", "ind.mcr": "MCR", "ind.scr_marche": "Market SCR", "ind.scr_contrepartie": "Counterparty SCR",
  "ind.scr_vie": "Life SCR", "ind.scr_sante": "Health SCR", "ind.scr_non_vie": "Non-life SCR",
  "ind.scr_operationnel": "Operational SCR", "ind.ratio_couverture_scr": "SCR coverage ratio",
  "ind.ratio_couverture_mcr": "MCR coverage ratio",
};

// Readiness labels come from backend rules (English). Translate by stable key.
const LABELS_FR: Record<string, string> = {
  "req.previous_report": "SFCR / RSR précédent (N-1)",
  "req.org_chart": "Organigramme",
  "req.supporting": "Documents d'appui (année en cours)",
  "req.qrt_s0501_n": "QRT S.05.01 (année en cours)",
  "req.qrt_s0501_n1": "QRT S.05.01 (N-1)",
  "req.investment_income_n": "Résultat des placements (QRT S.09.01 ou rapport financier, année en cours)",
  "req.financial_report_n": "Rapport financier (année en cours)",
  "req.qrt_s0201_n": "QRT S.02.01 (année en cours)",
  "req.qrt_s0201_n1": "QRT S.02.01 (N-1)",
  "req.qrt_tp_n": "QRT S.17.01 ou S.12.01 (année en cours)",
  "req.qrt_tp_n1": "QRT S.17.01 ou S.12.01 (N-1)",
  "req.actuarial_report": "Justificatifs actuariels",
  "req.narrative_sources": "Rapport précédent ou documents d'appui",
  "source.current_qrt": "QRT de l'année",
  "source.previous_qrt": "QRT N-1",
  "source.previous_sfcr": "SFCR / RSR précédent",
  "source.actuarial_report": "Rapport actuariel",
  "source.orsa": "Rapport ORSA",
  "source.policies": "Politiques écrites",
  "source.financial_report": "Rapport financier",
  "source.org_chart": "Organigramme",
  "source.group_template": "Template groupe",
};

/** A backend-provided label, translated by its stable key when we know it; generated
 * readiness labels ("QRT S.25.01 or S.28.01 (current year)") are translated by pattern. */
export function label(kind: "req" | "source", key: string, fallback: string): string {
  if (getLocale() === "en") return fallback;
  const known = LABELS_FR[`${kind}.${key}`];
  if (known) return known;
  return fallback.replace(" (current year)", " (année en cours)").replace(/ or /g, " ou ");
}

/** The workspace language, for API calls that return translated text. */
export function locale(): "fr" | "en" {
  return getLocale() === "en" ? "en" : "fr";
}

export function w(key: string, params: Record<string, unknown> = {}): string {
  const entry = (getLocale() === "en" ? EN[key] : undefined) ?? FR[key];
  if (entry === undefined) return key;
  return typeof entry === "function" ? entry(params) : entry;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return w("common.notAvailable");
  return new Intl.DateTimeFormat(getLocale() === "en" ? "en-GB" : "fr-FR",
    { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}
